import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  FileText,
  CheckCircle2,
  Clock,
  XCircle,
  AlertCircle,
  Eye,
  Download,
  Upload,
  Search,
  Filter,
  RefreshCw,
  User,
  Mail,
  Phone,
  ShieldCheck,
  FileCheck,
  FilePlus,
  Send,
  Trash2,
  ExternalLink,
  Folder,
  Grid,
  List,
  Sparkles,
  ChevronRight,
  ChevronDown,
  Plus,
  X,
  Building,
  Briefcase,
  Check,
  File,
  Info,
  CheckSquare,
  Square,
  Calendar,
  ArrowLeft,
  Share2
} from 'lucide-react';
import candidateService from '../../../api/services/candidateService';
import documentVerificationService from '../../../api/services/documentVerificationService';
import interviewService from '../../../api/services/interviewService';
import { documentTypeService } from '../../../api/crm/config/configService';
import { API_BASE_URL } from '../../../api/config';
import { useCustomAlert } from '../../../hooks/useCustomAlert';
import CustomAlert from '../../../components/common/CustomAlert';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';
import { formatDate } from '../../../utils/stringUtils';

// Helper to format display date safely
const formatDisplayDate = (value) => {
  if (!value) return 'N/A';
  try {
    return formatDate(value, { includeTime: true });
  } catch (error) {
    return 'N/A';
  }
};

// Helper to detect if a document is an image format (PNG, JPG, JPEG, WEBP, GIF, SVG)
const isImageDoc = (doc) => {
  if (!doc) return false;
  const name = (doc.documentName || doc.originalName || '').toLowerCase();
  const url = (doc.fileUrl || doc.url || '').toLowerCase();
  const type = (doc.fileType || '').toLowerCase();

  if (url.startsWith('data:image/')) return true;
  if (['png', 'jpg', 'jpeg', 'webp', 'gif', 'svg', 'image'].includes(type)) return true;
  if (name.endsWith('.png') || name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.webp') || name.endsWith('.gif') || name.endsWith('.svg')) {
    return true;
  }
  return false;
};

const getNormalizedFileType = (doc) => {
  if (!doc) return 'PDF';
  if (isImageDoc(doc)) {
    const name = (doc.documentName || doc.originalName || '').toLowerCase();
    if (name.endsWith('.png')) return 'PNG';
    if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'JPG';
    if (name.endsWith('.webp')) return 'WEBP';
    if (name.endsWith('.gif')) return 'GIF';
    if (name.endsWith('.svg')) return 'SVG';
    const typeStr = (doc.fileType || '').toUpperCase();
    return ['PNG', 'JPG', 'JPEG', 'WEBP', 'GIF', 'SVG'].includes(typeStr) ? typeStr : 'PNG';
  }
  return (doc.fileType || 'PDF').toUpperCase();
};

// Helper to trigger direct browser download from Blob payload
const downloadBlobAsFile = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 3000);
};

// Helper to convert Base64 data URL to Blob
const dataUrlToBlob = (dataUrl) => {
  try {
    const parts = dataUrl.split(',');
    const mimeMatch = parts[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
    const bstr = atob(parts[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  } catch (e) {
    return null;
  }
};

// Helper to generate a 100% standard-compliant, non-blank printable PDF document record
const generateDocumentRecordPdfBlob = (doc, customTitle) => {
  const sanitize = (text) => (text || '').replace(/[\\()]/g, '');
  const title = sanitize(customTitle || doc?.documentName || 'Candidate Document Record');
  const candidateName = sanitize(doc?.candidateName || 'Candidate');
  const candidateEmail = sanitize(doc?.candidateEmail || 'N/A');
  const candidatePhone = sanitize(doc?.candidatePhone || 'N/A');
  const jobTitle = sanitize(doc?.jobTitle || 'N/A');
  const department = sanitize(doc?.department || 'General');
  const category = sanitize(doc?.categoryLabel || 'Verification Document');
  const status = sanitize((doc?.status || 'pending').toUpperCase());
  const uploadDate = sanitize(formatDisplayDate(doc?.uploadedAt));
  const verifiedBy = sanitize(doc?.verifiedBy || 'HR Systems Audit');
  const auditNotes = sanitize(doc?.verificationNotes || 'Official candidate document record verified in HRMS.');

  const streamContent = [
    'BT',
    '/F1 18 Tf',
    '50 740 Td',
    '(MOBILOITTE HRMS - CANDIDATE DOCUMENT RECORD) Tj',
    '0 -15 Td',
    '/F2 9 Tf',
    '(Recruitment & Onboarding Verification System) Tj',
    '0 -25 Td',
    '/F1 13 Tf',
    `(${title}) Tj`,
    '0 -22 Td',
    '/F2 11 Tf',
    `(Candidate Name: ${candidateName}) Tj`,
    '0 -18 Td',
    `(Email Address: ${candidateEmail}) Tj`,
    '0 -18 Td',
    `(Phone Number: ${candidatePhone}) Tj`,
    '0 -18 Td',
    `(Applied Position: ${jobTitle}) Tj`,
    '0 -18 Td',
    `(Department: ${department}) Tj`,
    '0 -18 Td',
    `(Document Category: ${category}) Tj`,
    '0 -18 Td',
    `(Upload Date: ${uploadDate}) Tj`,
    '0 -18 Td',
    `(Verification Status: ${status}) Tj`,
    '0 -18 Td',
    `(Verified By: ${verifiedBy}) Tj`,
    '0 -24 Td',
    '/F1 10 Tf',
    '(Audit Verification Remarks:) Tj',
    '0 -15 Td',
    '/F2 10 Tf',
    `(${auditNotes}) Tj`,
    '0 -35 Td',
    '/F2 9 Tf',
    '(This document was verified and archived by Mobiloitte HRMS Recruitment Systems.) Tj',
    'ET'
  ].join('\n');

  const streamLength = streamContent.length;

  const objects = [];
  objects[1] = '1 0 obj\n<</Type /Catalog /Pages 2 0 R>>\nendobj';
  objects[2] = '2 0 obj\n<</Type /Pages /Kids [3 0 R] /Count 1>>\nendobj';
  objects[3] = '3 0 obj\n<</Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Resources <</Font <</F1 5 0 R /F2 6 0 R>>>> /Contents 4 0 R>>\nendobj';
  objects[4] = `4 0 obj\n<</Length ${streamLength}>>\nstream\n${streamContent}\nendstream\nendobj`;
  objects[5] = '5 0 obj\n<</Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold>>\nendobj';
  objects[6] = '6 0 obj\n<</Type /Font /Subtype /Type1 /BaseFont /Helvetica>>\nendobj';

  let pdfText = '%PDF-1.4\n';
  const offsets = [];

  for (let i = 1; i <= 6; i++) {
    offsets[i] = pdfText.length;
    pdfText += objects[i] + '\n';
  }

  const xrefStart = pdfText.length;
  pdfText += 'xref\n0 7\n';
  pdfText += '0000000000 65535 f \n';
  for (let i = 1; i <= 6; i++) {
    pdfText += String(offsets[i]).padStart(10, '0') + ' 00000 n \n';
  }
  pdfText += `trailer\n<</Size 7 /Root 1 0 R>>\nstartxref\n${xrefStart}\n%%EOF`;

  return new Blob([pdfText], { type: 'application/pdf' });
};

// Standard Document Checkbox Categories for Request Modal & Candidate Details Matrix
const STANDARD_REQUEST_DOCUMENTS = [
  { id: 'pan_card', name: 'PAN Card', category: 'identity', categoryLabel: 'Identity Proof', defaultChecked: true },
  { id: 'aadhar_card', name: 'Aadhar Card (Front & Back)', category: 'identity', categoryLabel: 'Identity Proof', defaultChecked: true },
  { id: 'passport', name: 'Passport / Government ID', category: 'identity', categoryLabel: 'Identity Proof', defaultChecked: false },
  { id: 'degree_certificate', name: 'Degree Certificate / Graduation Marksheet', category: 'education', categoryLabel: 'Educational Certificate', defaultChecked: true },
  { id: 'school_certificates', name: '10th & 12th Passing Certificates', category: 'education', categoryLabel: 'Educational Certificate', defaultChecked: false },
  { id: 'experience_letter', name: 'Relieving & Experience Letter (Previous Company)', category: 'experience', categoryLabel: 'Experience Letter', defaultChecked: true },
  { id: 'payslips', name: 'Last 3 Months Payslips', category: 'experience', categoryLabel: 'Salary & Payslips', defaultChecked: false },
  { id: 'bank_cheque', name: 'Cancelled Cheque / Bank Passbook Details', category: 'bank', categoryLabel: 'Bank Details', defaultChecked: true },
  { id: 'tax_form16', name: 'Form 16 / Tax Declaration Certificate', category: 'bank', categoryLabel: 'Financial Details', defaultChecked: false },
  { id: 'profile_photo', name: 'Passport Size Photograph', category: 'other', categoryLabel: 'Profile Photo', defaultChecked: true },
];

// Document Category mapping
const DOCUMENT_CATEGORIES = [
  { id: 'all', label: 'All Categories' },
  { id: 'resume', label: 'Resume / CV' },
  { id: 'identity', label: 'Identity Proof (PAN/Aadhar/Passport)' },
  { id: 'education', label: 'Educational Certificates' },
  { id: 'experience', label: 'Experience & Relieving Letters' },
  { id: 'offer', label: 'Offer & Contract Documents' },
  { id: 'bank', label: 'Bank & Financial Details' },
  { id: 'other', label: 'Other Documents' },
];

const STATUS_FILTERS = [
  { id: 'all', label: 'All Statuses' },
  { id: 'approved', label: 'Verified / Approved' },
  { id: 'pending', label: 'Pending Verification' },
  { id: 'rejected', label: 'Rejected / Action Required' },
  { id: 'missing', label: 'Missing / Requested' },
];

// Fallback seed documents for candidates
const FALLBACK_CANDIDATE_DOCUMENTS = [
  {
    id: 'doc_101',
    candidateId: 'cand_1',
    candidateName: 'Salman Khan',
    candidateEmail: 'salman.khan@example.com',
    candidatePhone: '+91 98765 43210',
    jobTitle: 'Software Engineer',
    department: 'Engineering',
    candidateStage: 'documentation',
    documentName: 'Salman_Khan_Resume.pdf',
    documentType: 'resume',
    categoryLabel: 'Resume / CV',
    fileSize: '1.8 MB',
    fileType: 'PDF',
    fileUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
    uploadedAt: new Date(Date.now() - 86400000 * 2).toISOString(),
    status: 'approved',
    verifiedBy: 'Super Admin',
    verifiedAt: new Date(Date.now() - 86400000 * 1).toISOString(),
    verificationNotes: 'Resume verified and matched experience.',
  },
  {
    id: 'doc_102',
    candidateId: 'cand_1',
    candidateName: 'Salman Khan',
    candidateEmail: 'salman.khan@example.com',
    candidatePhone: '+91 98765 43210',
    jobTitle: 'Software Engineer',
    department: 'Engineering',
    candidateStage: 'documentation',
    documentName: 'PAN_Card_Salman.pdf',
    documentType: 'pan_card',
    categoryLabel: 'Identity Proof',
    fileSize: '850 KB',
    fileType: 'PDF',
    fileUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
    uploadedAt: new Date(Date.now() - 86400000 * 2).toISOString(),
    status: 'approved',
    verifiedBy: 'Super Admin',
    verifiedAt: new Date(Date.now() - 3600000 * 5).toISOString(),
    verificationNotes: 'PAN card verified with income tax records.',
  },
  {
    id: 'doc_103',
    candidateId: 'cand_1',
    candidateName: 'Salman Khan',
    candidateEmail: 'salman.khan@example.com',
    candidatePhone: '+91 98765 43210',
    jobTitle: 'Software Engineer',
    department: 'Engineering',
    candidateStage: 'documentation',
    documentName: 'Degree_Certificate_BTech.pdf',
    documentType: 'degree_certificate',
    categoryLabel: 'Educational Certificate',
    fileSize: '2.4 MB',
    fileType: 'PDF',
    fileUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
    uploadedAt: new Date(Date.now() - 86400000 * 1).toISOString(),
    status: 'pending',
    verifiedBy: null,
    verifiedAt: null,
    verificationNotes: 'Awaiting university degree verification check.',
  },
  {
    id: 'doc_104',
    candidateId: 'cand_1',
    candidateName: 'Salman Khan',
    candidateEmail: 'salman.khan@example.com',
    candidatePhone: '+91 98765 43210',
    jobTitle: 'Software Engineer',
    department: 'Engineering',
    candidateStage: 'documentation',
    documentName: 'Relieving_Letter_Previous_Company.pdf',
    documentType: 'experience_letter',
    categoryLabel: 'Experience Letter',
    fileSize: '1.1 MB',
    fileType: 'PDF',
    fileUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
    uploadedAt: new Date(Date.now() - 86400000 * 1).toISOString(),
    status: 'pending',
    verifiedBy: null,
    verifiedAt: null,
    verificationNotes: '',
  },
  {
    id: 'doc_105',
    candidateId: 'cand_1',
    candidateName: 'Salman Khan',
    candidateEmail: 'salman.khan@example.com',
    candidatePhone: '+91 98765 43210',
    jobTitle: 'Software Engineer',
    department: 'Engineering',
    candidateStage: 'documentation',
    documentName: 'Aadhar_Card_Front_Back.pdf',
    documentType: 'aadhar_card',
    categoryLabel: 'Identity Proof',
    fileSize: '1.5 MB',
    fileType: 'PDF',
    fileUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
    uploadedAt: new Date(Date.now() - 3600000 * 6).toISOString(),
    status: 'approved',
    verifiedBy: 'HR Systems',
    verifiedAt: new Date(Date.now() - 3600000 * 2).toISOString(),
    verificationNotes: 'Aadhar Card verified.',
  },
  {
    id: 'doc_106',
    candidateId: 'cand_2',
    candidateName: 'Dummy Candidate',
    candidateEmail: 'dummy.candidate@example.com',
    candidatePhone: '+91 98123 45678',
    jobTitle: 'UI/UX Designer',
    department: 'Design',
    candidateStage: 'interviewed',
    documentName: 'Dummy_Candidate_Portfolio_Resume.pdf',
    documentType: 'resume',
    categoryLabel: 'Resume / CV',
    fileSize: '3.2 MB',
    fileType: 'PDF',
    fileUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
    uploadedAt: new Date(Date.now() - 86400000 * 3).toISOString(),
    status: 'approved',
    verifiedBy: 'Prince Lkasfdlkj',
    verifiedAt: new Date(Date.now() - 86400000 * 2).toISOString(),
    verificationNotes: 'Portfolio & Resume approved by HR.',
  },
  {
    id: 'doc_107',
    candidateId: 'cand_2',
    candidateName: 'Dummy Candidate',
    candidateEmail: 'dummy.candidate@example.com',
    candidatePhone: '+91 98123 45678',
    jobTitle: 'UI/UX Designer',
    department: 'Design',
    candidateStage: 'interviewed',
    documentName: 'Aadhar_Card_Front_Back.jpg',
    documentType: 'aadhar_card',
    categoryLabel: 'Identity Proof',
    fileSize: '920 KB',
    fileType: 'JPG',
    fileUrl: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&w=600&q=80',
    uploadedAt: new Date(Date.now() - 86400000 * 2).toISOString(),
    status: 'rejected',
    verifiedBy: 'Prince Lkasfdlkj',
    verifiedAt: new Date(Date.now() - 86400000 * 1).toISOString(),
    verificationNotes: 'Aadhar image blurriness detected. Please upload clear scan.',
  },
  {
    id: 'doc_108',
    candidateId: 'cand_3',
    candidateName: 'Prince Kumar',
    candidateEmail: 'prince.kumar@example.com',
    candidatePhone: '+91 97654 32109',
    jobTitle: 'Full Stack Developer',
    department: 'Engineering',
    candidateStage: 'offered',
    documentName: 'Signed_Offer_Acceptance_Letter.pdf',
    documentType: 'offer',
    categoryLabel: 'Offer Document',
    fileSize: '650 KB',
    fileType: 'PDF',
    fileUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
    uploadedAt: new Date(Date.now() - 3600000 * 12).toISOString(),
    status: 'approved',
    verifiedBy: 'Super Admin',
    verifiedAt: new Date(Date.now() - 3600000 * 2).toISOString(),
    verificationNotes: 'Duly signed offer letter submitted.',
  },
  {
    id: 'doc_109',
    candidateId: 'cand_3',
    candidateName: 'Prince Kumar',
    candidateEmail: 'prince.kumar@example.com',
    candidatePhone: '+91 97654 32109',
    jobTitle: 'Full Stack Developer',
    department: 'Engineering',
    candidateStage: 'offered',
    documentName: 'Cancelled_Cheque_Bank_Details.pdf',
    documentType: 'bank_cheque',
    categoryLabel: 'Bank & Financial',
    fileSize: '410 KB',
    fileType: 'PDF',
    fileUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
    uploadedAt: new Date(Date.now() - 3600000 * 8).toISOString(),
    status: 'pending',
    verifiedBy: null,
    verifiedAt: null,
    verificationNotes: 'Verifying account number & IFSC code.',
  }
];

const CandidateDocumentation = () => {
  const { alertState, showSuccess, showError, hideAlert } = useCustomAlert();

  // State Management
  const [documents, setDocuments] = useState([]);
  const [candidatesList, setCandidatesList] = useState([]);
  const [completedCandidateIds, setCompletedCandidateIds] = useState(new Set());
  const [uncompletedCandidateIds, setUncompletedCandidateIds] = useState(new Set());
  const [uncompletedCandidateNames, setUncompletedCandidateNames] = useState(new Set());
  const [masterDocTypes, setMasterDocTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filters State
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedCandidateId, setSelectedCandidateId] = useState('all');
  const [viewMode, setViewMode] = useState('grouped');

  // Expand / Collapse State for Candidate Grouped Cards
  const [expandedCandidateKeys, setExpandedCandidateKeys] = useState(new Set());

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Modals & Selection State
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [activePdfBlobUrl, setActivePdfBlobUrl] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingDoc, setRejectingDoc] = useState(null);
  const [rejectionNotes, setRejectionNotes] = useState('');

  // Selected Candidate for Dedicated Sub-Page Details View
  const [selectedCandidateDetails, setSelectedCandidateDetails] = useState(null);

  // URL Search Params Sync for Candidate ID Routing (?tab=documentation&candidateId=...)
  const [searchParams, setSearchParams] = useSearchParams();
  const candidateIdFromUrl = searchParams.get('candidateId');

  const handleOpenCandidateDossier = useCallback((candGroup) => {
    if (!candGroup) return;
    setSelectedCandidateDetails(candGroup);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (candGroup.candidateId) {
        next.set('candidateId', candGroup.candidateId);
      }
      return next;
    }, { replace: false });
  }, [setSearchParams]);

  const handleCloseCandidateDossier = useCallback(() => {
    setSelectedCandidateDetails(null);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('candidateId');
      return next;
    }, { replace: false });
  }, [setSearchParams]);

  // Set PDF / Document Preview URL whenever previewDocument modal is opened
  useEffect(() => {
    if (!previewDocument) {
      setActivePdfBlobUrl(null);
      return;
    }

    if (isImageDoc(previewDocument)) {
      setActivePdfBlobUrl(null);
      return;
    }

    const fileUrl = previewDocument.fileUrl || previewDocument.url;

    // 1. If document has a valid uploaded file URL (S3, HTTP, Blob, Data), use it directly for preview!
    if (fileUrl && fileUrl !== '#' && fileUrl !== 'null' && fileUrl !== 'undefined') {
      setActivePdfBlobUrl(fileUrl);
      return;
    }

    // 2. Executive HR Audit Verification Record & Certificate fallback
    const docTitle = previewDocument.documentName || 'Candidate Document Record';
    const statusUpper = (previewDocument.status || 'pending').toUpperCase();
    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${docTitle}</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding: 30px; background: #0f172a; color: #1e293b; display: flex; justify-content: center; align-items: center; min-height: 85vh; margin: 0; }
            .card { background: #ffffff; border-radius: 20px; padding: 36px; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.3); max-width: 540px; width: 100%; border: 2px solid #e2e8f0; position: relative; overflow: hidden; }
            .header-bar { display: flex; align-items: center; justify-content: space-between; border-bottom: 2px solid #3b82f6; padding-bottom: 14px; margin-bottom: 20px; }
            .company-title { font-size: 18px; font-weight: 900; color: #1e3a8a; margin: 0; letter-spacing: -0.5px; }
            .sub-title { font-size: 10px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 1px; }
            .badge-container { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
            .badge { display: inline-block; padding: 5px 12px; background: #eff6ff; color: #1d4ed8; font-weight: 800; border-radius: 9999px; font-size: 10px; text-transform: uppercase; border: 1px solid #bfdbfe; }
            .status-badge { display: inline-block; padding: 5px 12px; border-radius: 9999px; font-size: 10px; font-weight: 800; text-transform: uppercase; }
            .status-pending { background: #fffbeb; color: #b45309; border: 1px solid #fde68a; }
            .status-approved { background: #ecfdf5; color: #047857; border: 1px solid #a7f3d0; }
            .status-rejected { background: #fff1f2; color: #be123c; border: 1px solid #fecdd3; }
            .doc-name { font-size: 20px; font-weight: 800; color: #0f172a; margin: 0 0 16px 0; word-break: break-word; }
            .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; background: #f8fafc; padding: 16px; border-radius: 12px; border: 1px solid #f1f5f9; }
            .item { display: flex; flex-direction: column; gap: 2px; }
            .label { color: #64748b; font-weight: 600; font-size: 10px; text-transform: uppercase; }
            .value { font-weight: 800; color: #0f172a; font-size: 12px; word-break: break-word; }
            .notes-box { margin-top: 14px; padding: 12px; background: #f0f9ff; border: 1px solid #bae6fd; border-radius: 10px; font-size: 11px; color: #0369a1; font-weight: 600; }
            .footer { margin-top: 20px; padding-top: 14px; border-top: 1px solid #e2e8f0; font-size: 10px; color: #94a3b8; text-align: center; font-weight: 600; display: flex; justify-content: space-between; }
          </style>
        </head>
        <body>
          <div className="card">
            <div className="header-bar">
              <div>
                <h1 className="company-title">MOBILOITTE HRMS</h1>
                <span className="sub-title">Recruitment & Document Verification System</span>
              </div>
            </div>
            <div className="badge-container">
              <span className="badge">Candidate Document Audit</span>
              <span className="status-badge ${previewDocument.status === 'approved' ? 'status-approved' : previewDocument.status === 'rejected' ? 'status-rejected' : 'status-pending'}">
                ${statusUpper}
              </span>
            </div>
            <h2 className="doc-name">${docTitle}</h2>
            <div className="grid">
              <div className="item"><span className="label">Candidate Name</span><span className="value">${previewDocument.candidateName || 'Candidate'}</span></div>
              <div className="item"><span className="label">Applied Position</span><span className="value">${previewDocument.jobTitle || 'N/A'}</span></div>
              <div className="item"><span className="label">Email Address</span><span className="value">${previewDocument.candidateEmail || 'N/A'}</span></div>
              <div className="item"><span className="label">Category</span><span className="value">${previewDocument.categoryLabel || 'Master Document'}</span></div>
              <div className="item"><span className="label">Uploaded / Recorded</span><span className="value">${formatDisplayDate(previewDocument.uploadedAt)}</span></div>
              <div className="item"><span className="label">Verified By</span><span className="value">${previewDocument.verifiedBy || 'HR Audit Team'}</span></div>
            </div>
            ${previewDocument.verificationNotes ? `<div className="notes-box"><strong>Audit Note:</strong> ${previewDocument.verificationNotes}</div>` : ''}
            <div className="footer">
              <span>Official HR Verification Record</span>
              <span>Mobiloitte HRMS</span>
            </div>
          </div>
        </body>
      </html>
    `;
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const blobUrl = URL.createObjectURL(blob);
    setActivePdfBlobUrl(blobUrl);

    return () => {
      if (blobUrl.startsWith('blob:')) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [previewDocument]);

  // Form states for New Upload Modal
  const [uploadCandidateId, setUploadCandidateId] = useState('');
  const [uploadCategory, setUploadCategory] = useState('identity');
  const [uploadDocName, setUploadDocName] = useState('');
  const [uploadFile, setUploadFile] = useState(null);

  // Request Document Form State
  const [reqCandidateId, setReqCandidateId] = useState('');
  const [selectedReqDocIds, setSelectedReqDocIds] = useState([]);
  const [customReqDocName, setCustomReqDocName] = useState('');
  const [customReqDocsList, setCustomReqDocsList] = useState([]);
  const [reqDueDate, setReqDueDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 5);
    return d.toISOString().split('T')[0];
  });
  const [reqNote, setReqNote] = useState('Please upload these required documents to complete your onboarding verification.');
  const [isSendingRequest, setIsSendingRequest] = useState(false);

  // Load Data
  const loadDocumentationData = useCallback(async (isSilent = false) => {
    if (!isSilent) setLoading(true);
    try {
      // 1. Fetch Candidates from Backend
      const candRes = await candidateService.getCandidates({ limit: 200 });
      let realCandidates = [];
      if (candRes && (candRes.data?.candidates || candRes.candidates || Array.isArray(candRes.data))) {
        realCandidates = candRes.data?.candidates || candRes.candidates || (Array.isArray(candRes.data) ? candRes.data : []);
      }
      setCandidatesList(realCandidates);

      // Fetch Interviews to identify candidates whose interview is COMPLETED vs SCHEDULED/UNCOMPLETED!
      const compIds = new Set();
      const uncompIds = new Set();
      const uncompNames = new Set();
      try {
        const intRes = await interviewService.getInterviews({ limit: 300 });
        let rawInts = [];
        if (intRes?.data?.interviews) rawInts = intRes.data.interviews;
        else if (intRes?.interviews) rawInts = intRes.interviews;
        else if (intRes?.data?.items) rawInts = intRes.data.items;
        else if (intRes?.items) rawInts = intRes.items;
        else if (Array.isArray(intRes?.data)) rawInts = intRes.data;
        else if (Array.isArray(intRes)) rawInts = intRes;

        rawInts.forEach(item => {
          const st = (item.status || '').toLowerCase();
          const isComp = item.iscompleted || st === 'completed';
          const cand = item.candidate;
          const cId = typeof cand === 'object' ? (cand?._id || cand?.id) : (cand || item.candidateId);
          const cName = item.candidateName || (typeof cand === 'object' ? `${cand?.firstName || ''} ${cand?.lastName || ''}`.trim() : '');

          const rounds = item.rounds || [];
          const hasUncompletedRound = rounds.some(r => (r.status || '').toLowerCase() === 'scheduled' || (r.status || '').toLowerCase() === 'rescheduled');

          if (hasUncompletedRound || st === 'scheduled' || st === 'rescheduled') {
            if (cId) uncompIds.add(String(cId));
            if (cName) uncompNames.add(cName.toLowerCase().trim());
          } else if (isComp) {
            if (cId) compIds.add(String(cId));
          }
        });
      } catch (err) {
        console.warn('Interviews fetch note:', err);
      }
      setCompletedCandidateIds(compIds);
      setUncompletedCandidateIds(uncompIds);
      setUncompletedCandidateNames(uncompNames);

      // Fetch Master Document Types configured in HRMS Configuration Masters!
      try {
        const masterRes = await documentTypeService.getAll({ isActive: true });
        let rawMasters = [];
        if (Array.isArray(masterRes)) rawMasters = masterRes;
        else if (masterRes?.data?.documentTypes) rawMasters = masterRes.data.documentTypes;
        else if (masterRes?.data?.items) rawMasters = masterRes.data.items;
        else if (masterRes?.data) rawMasters = Array.isArray(masterRes.data) ? masterRes.data : [];
        else if (masterRes?.documentTypes) rawMasters = masterRes.documentTypes;
        else if (masterRes?.items) rawMasters = masterRes.items;

        const formattedMasters = rawMasters.map(m => ({
          id: (m.code || m._id || m.name || '').toLowerCase().replace(/\s+/g, '_'),
          name: m.name || 'Custom Document Type',
          category: 'master',
          categoryLabel: m.description || m.name || 'Configured Master Document Type',
          defaultChecked: true
        }));

        setMasterDocTypes(formattedMasters);
        if (formattedMasters.length > 0) {
          setSelectedReqDocIds(formattedMasters.map(m => m.id));
        }
      } catch (masterErr) {
        console.warn('Master document types fetch note:', masterErr);
      }

      // 2. Fetch Document Verifications from backend service
      let fetchedDocs = [];
      try {
        const docRes = await documentVerificationService.getDocuments({ limit: 200 });
        if (docRes && docRes.success && Array.isArray(docRes.data)) {
          fetchedDocs = docRes.data.map(d => ({
            id: d._id || d.id,
            candidateId: d.candidate?._id || d.candidateId || 'cand_unk',
            candidateName: d.candidateName || (d.candidate ? `${d.candidate.firstName || ''} ${d.candidate.lastName || ''}`.trim() : 'Candidate'),
            candidateEmail: d.candidateEmail || d.candidate?.email || 'N/A',
            candidatePhone: d.candidate?.phone || 'N/A',
            jobTitle: d.candidate?.jobTitle || 'Candidate',
            department: d.candidate?.department || 'General',
            candidateStage: d.candidate?.recruitmentStatus || 'documentation',
            documentName: d.documentName || 'Document.pdf',
            documentType: d.documentType || 'other',
            categoryLabel: d.documentType ? d.documentType.toUpperCase() : 'Other Document',
            fileSize: d.fileSize || '1.0 MB',
            fileType: d.fileType || 'PDF',
            fileUrl: d.documentUrl || d.fileUrl || '#',
            uploadedAt: d.uploadedDate || d.createdAt || new Date().toISOString(),
            status: d.status === 'approved' ? 'approved' : d.status === 'rejected' ? 'rejected' : d.status === 'missing' ? 'missing' : 'pending',
            verifiedBy: d.verifiedBy?.name || d.verifiedBy || 'HR Team',
            verifiedAt: d.verifiedAt || null,
            verificationNotes: d.verificationNotes || ''
          }));
        }
      } catch (err) {
        console.warn('DocumentVerification API note:', err);
      }

      // 3. Extract Documents directly attached to Candidate objects in candidate database
      const extractedFromCandidates = [];
      realCandidates.forEach(cand => {
        const cName = `${cand.firstName || ''} ${cand.lastName || ''}`.trim() || cand.candidateId || 'Candidate';

        // Check Resume
        if (cand.resume || cand.resumeUrl) {
          extractedFromCandidates.push({
            id: `cand_res_${cand._id || cand.id || cand.candidateId}`,
            candidateId: cand._id || cand.id || cand.candidateId,
            candidateName: cName,
            candidateEmail: cand.email || 'N/A',
            candidatePhone: cand.phone || 'N/A',
            jobTitle: cand.jobTitle || 'Candidate',
            department: cand.department || 'General',
            candidateStage: cand.recruitmentStatus || 'applied',
            documentName: cand.resume?.originalName || cand.resume?.filename || `${cName.replace(/\s+/g, '_')}_Resume.pdf`,
            documentType: 'resume',
            categoryLabel: 'Resume / CV',
            fileSize: cand.resume?.size ? `${(cand.resume.size / 1024 / 1024).toFixed(1)} MB` : '1.5 MB',
            fileType: 'PDF',
            fileUrl: cand.resume?.url || cand.resumeUrl || '#',
            uploadedAt: cand.appliedDate || cand.createdAt || new Date().toISOString(),
            status: cand.isDocumentVerified ? 'approved' : 'approved',
            verifiedBy: 'HR Systems',
            verifiedAt: cand.appliedDate || new Date().toISOString(),
            verificationNotes: 'Candidate Resume',
          });
        }

        // Check documents array inside Candidate
        if (Array.isArray(cand.documents) && cand.documents.length > 0) {
          cand.documents.forEach((d, idx) => {
            extractedFromCandidates.push({
              id: d._id || d.id || `cand_doc_${cand._id}_${idx}`,
              candidateId: cand._id || cand.id || cand.candidateId,
              candidateName: cName,
              candidateEmail: cand.email || 'N/A',
              candidatePhone: cand.phone || 'N/A',
              jobTitle: cand.jobTitle || 'Candidate',
              department: cand.department || 'General',
              candidateStage: cand.recruitmentStatus || 'documentation',
              documentName: d.documentName || d.originalName || d.filename || `Document_${idx + 1}.pdf`,
              documentType: d.documentType || 'identity',
              categoryLabel: d.categoryLabel || (d.documentType ? d.documentType.replace('_', ' ').toUpperCase() : 'Document'),
              fileSize: typeof d.fileSize === 'string' ? d.fileSize : (d.fileSize ? `${(d.fileSize / 1024 / 1024).toFixed(1)} MB` : '1.2 MB'),
              fileType: d.fileType || 'PDF',
              fileUrl: d.fileUrl || d.url || '#',
              uploadedAt: d.uploadedAt || new Date().toISOString(),
              status: d.status || d.verificationStatus || 'pending',
              verifiedBy: d.verifiedBy || null,
              verifiedAt: d.verifiedAt || null,
              verificationNotes: d.verificationNotes || ''
            });
          });
        }
      });

      // Read candidate uploaded documents from localStorage sync
      let localUploadedDocs = [];
      try {
        localUploadedDocs = JSON.parse(localStorage.getItem('hrms_candidate_uploaded_docs') || '[]');
      } catch(e) {}

      // Combine fetched API docs + candidate docs + local uploaded docs
      const combinedMap = new Map();

      // Only use seed fallbacks if zero real candidate documents exist in database
      if (extractedFromCandidates.length === 0 && fetchedDocs.length === 0 && localUploadedDocs.length === 0) {
        FALLBACK_CANDIDATE_DOCUMENTS.forEach(doc => combinedMap.set(doc.id, doc));
      }

      // Add real database candidate documents
      extractedFromCandidates.forEach(doc => combinedMap.set(doc.id, doc));

      // Add API documents
      fetchedDocs.forEach(doc => combinedMap.set(doc.id, doc));

      // Add candidate uploaded docs via email portal sync
      localUploadedDocs.forEach(doc => {
        combinedMap.set(doc.id, doc);
      });

      const finalDocsList = Array.from(combinedMap.values());
      setDocuments(finalDocsList);

    } catch (error) {
      console.error('Error loading candidate documentation:', error);
      showError('Failed to refresh candidate documents: ' + (error.message || 'Error'));
      setDocuments(FALLBACK_CANDIDATE_DOCUMENTS);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [showError]);

  useEffect(() => {
    // Initial data load on mount with loader
    loadDocumentationData(false);

    // Silent refresh when candidate submits documents in another tab/portal
    const handleStorageChange = (e) => {
      if (!e || !e.key || e.key === 'hrms_candidate_uploaded_docs') {
        loadDocumentationData(true);
      }
    };

    // Silent refresh when HR returns focus to HRMS tab
    const handleWindowFocus = () => {
      loadDocumentationData(true);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [loadDocumentationData]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadDocumentationData(true);
  };

  // Filtered Documents Logic
  const filteredDocuments = useMemo(() => {
    return documents.filter(doc => {
      // EXCLUDE candidate if their interview is scheduled or not completed yet!
      const candIdStr = String(doc.candidateId || '');
      const candNameStr = (doc.candidateName || '').toLowerCase().trim();
      if (uncompletedCandidateIds.has(candIdStr) || uncompletedCandidateNames.has(candNameStr)) {
        return false;
      }

      // Search
      const search = searchTerm.toLowerCase().trim();
      if (search) {
        const matchesSearch =
          doc.candidateName.toLowerCase().includes(search) ||
          doc.candidateEmail.toLowerCase().includes(search) ||
          doc.documentName.toLowerCase().includes(search) ||
          doc.jobTitle.toLowerCase().includes(search) ||
          (doc.verificationNotes && doc.verificationNotes.toLowerCase().includes(search));
        if (!matchesSearch) return false;
      }

      // Category
      if (selectedCategory !== 'all') {
        if (selectedCategory === 'resume' && doc.documentType !== 'resume') return false;
        if (selectedCategory === 'identity' && !['identity', 'pan_card', 'aadhar_card', 'passport'].includes(doc.documentType)) return false;
        if (selectedCategory === 'education' && !['education', 'degree_certificate', 'school_certificates'].includes(doc.documentType)) return false;
        if (selectedCategory === 'experience' && !['experience', 'experience_letter', 'payslips'].includes(doc.documentType)) return false;
        if (selectedCategory === 'offer' && doc.documentType !== 'offer') return false;
        if (selectedCategory === 'bank' && !['bank', 'bank_cheque', 'tax_form16'].includes(doc.documentType)) return false;
      }

      // Status
      if (selectedStatus !== 'all') {
        if (selectedStatus === 'approved' && doc.status !== 'approved') return false;
        if (selectedStatus === 'pending' && doc.status !== 'pending') return false;
        if (selectedStatus === 'rejected' && doc.status !== 'rejected') return false;
        if (selectedStatus === 'missing' && doc.status !== 'missing') return false;
      }

      // Candidate Filter
      if (selectedCandidateId !== 'all' && doc.candidateId !== selectedCandidateId) {
        return false;
      }

      return true;
    });
  }, [documents, searchTerm, selectedCategory, selectedStatus, selectedCandidateId, uncompletedCandidateIds, uncompletedCandidateNames]);

  // Grouped Candidates Data - Unifies ALL documents of a single candidate together!
  const candidateGroupedList = useMemo(() => {
    const map = new Map();

    filteredDocuments.forEach(doc => {
      const key = doc.candidateName.toLowerCase().trim();

      if (!map.has(key)) {
        map.set(key, {
          key,
          candidateId: doc.candidateId,
          candidateName: doc.candidateName,
          candidateEmail: doc.candidateEmail,
          candidatePhone: doc.candidatePhone,
          jobTitle: doc.jobTitle,
          department: doc.department,
          candidateStage: doc.candidateStage,
          documents: []
        });
      }

      map.get(key).documents.push(doc);
    });

    return Array.from(map.values());
  }, [filteredDocuments]);

  // Auto-sync selected candidate from URL query parameter (?tab=documentation&candidateId=...)
  useEffect(() => {
    if (!candidateIdFromUrl || candidateIdFromUrl === 'all') {
      if (selectedCandidateDetails) {
        setSelectedCandidateDetails(null);
      }
      return;
    }

    if (candidateGroupedList && candidateGroupedList.length > 0) {
      const target = candidateGroupedList.find(c =>
        c.candidateId === candidateIdFromUrl ||
        c.candidateId === `cand_${candidateIdFromUrl}` ||
        c.candidateId === candidateIdFromUrl.replace('cand_', '') ||
        c.key === candidateIdFromUrl.toLowerCase().trim() ||
        c.candidateName.toLowerCase().trim().includes(candidateIdFromUrl.toLowerCase().trim())
      );
      if (target && (!selectedCandidateDetails || selectedCandidateDetails.candidateId !== target.candidateId)) {
        setSelectedCandidateDetails(target);
      }
    }
  }, [candidateIdFromUrl, candidateGroupedList]);

  // Filter candidates list to ONLY show candidates whose interview is COMPLETED!
  const completedInterviewCandidates = useMemo(() => {
    if (!candidatesList || candidatesList.length === 0) return [];

    return candidatesList.filter(c => {
      const cid = String(c._id || c.id || c.candidateId || '');
      const firstName = (c.firstName || '').toLowerCase().trim();
      const lastName = (c.lastName || '').toLowerCase().trim();
      const fullName = `${firstName} ${lastName}`.trim();

      // If candidate has an uncompleted or scheduled interview, EXCLUDE THEM completely!
      if (uncompletedCandidateIds.has(cid)) return false;
      if (fullName && uncompletedCandidateNames.has(fullName)) return false;

      for (const unName of uncompletedCandidateNames) {
        if (unName && (fullName.includes(unName) || unName.includes(fullName) || (firstName && unName.startsWith(firstName)))) {
          return false;
        }
      }

      // Check if candidate has completed interview in database
      if (completedCandidateIds.has(cid)) {
        return true;
      }

      // Check stage fallback, but only if candidate does NOT have a scheduled interview
      const stage = (c.recruitmentStatus || c.candidateStage || '').toLowerCase();
      if (['interviewed', 'documentation', 'offered', 'offer', 'hired'].includes(stage)) {
        return true;
      }

    });
  }, [candidatesList, completedCandidateIds, uncompletedCandidateIds, uncompletedCandidateNames]);

  // Dynamic Document Requirements checklist - ONLY Master Document Types + Custom Requirements (NO HARDCODED STATIC DOCUMENTS)
  const availableRequestDocuments = useMemo(() => {
    return [...masterDocTypes, ...customReqDocsList];
  }, [masterDocTypes, customReqDocsList]);

  // KPI Metrics Calculation - Computed from REAL-TIME candidate database documents
  const metrics = useMemo(() => {
    const total = documents.length;
    const verified = documents.filter(d => d.status === 'approved' || d.status === 'verified').length;
    const pending = documents.filter(d => d.status === 'pending').length;
    const rejected = documents.filter(d => d.status === 'rejected').length;
    const completionRate = total > 0 ? Math.round((verified / total) * 100) : 0;
    return { total, verified, pending, rejected, completionRate };
  }, [documents]);

  // Pagination Logic for Grouped View
  const totalItemsCount = viewMode === 'grouped' ? candidateGroupedList.length : filteredDocuments.length;
  const totalPages = Math.ceil(totalItemsCount / itemsPerPage) || 1;

  const paginatedCandidateGroups = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return candidateGroupedList.slice(start, start + itemsPerPage);
  }, [candidateGroupedList, currentPage, itemsPerPage]);

  const paginatedFlatDocuments = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredDocuments.slice(start, start + itemsPerPage);
  }, [filteredDocuments, currentPage, itemsPerPage]);

  // Selection Handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedDocIds(filteredDocuments.map(d => d.id));
    } else {
      setSelectedDocIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedDocIds(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const handleSelectAllCandidateDocs = (candDocs, e) => {
    const docIds = candDocs.map(d => d.id);
    if (e.target.checked) {
      setSelectedDocIds(prev => Array.from(new Set([...prev, ...docIds])));
    } else {
      setSelectedDocIds(prev => prev.filter(id => !docIds.includes(id)));
    }
  };

  // Request Document Checkbox Handlers inside Modal
  const toggleReqDocCheckbox = (docId) => {
    setSelectedReqDocIds(prev =>
      prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]
    );
  };

  const handleSelectAllReqDocs = () => {
    setSelectedReqDocIds(availableRequestDocuments.map(d => d.id));
  };

  const handleClearAllReqDocs = () => {
    setSelectedReqDocIds([]);
  };

  const handleAddCustomReqDoc = () => {
    if (!customReqDocName.trim()) return;
    const newId = `custom_req_${Date.now()}`;
    const newCustomDoc = {
      id: newId,
      name: customReqDocName.trim(),
      category: 'other',
      categoryLabel: 'Custom Document',
      defaultChecked: true
    };
    setCustomReqDocsList(prev => [...prev, newCustomDoc]);
    setSelectedReqDocIds(prev => [...prev, newId]);
    setCustomReqDocName('');
  };

  // Actions
  const handleApproveDocument = async (docId) => {
    try {
      try {
        await documentVerificationService.verifyDocument(docId, 'approve', 'Verified by HR Admin');
      } catch (err) {
        console.log('Backend sync note:', err);
      }

      const updatedFields = {
        status: 'approved',
        verifiedBy: 'Super Admin',
        verifiedAt: new Date().toISOString(),
        verificationNotes: 'Verified and approved.'
      };

      setDocuments(prev =>
        prev.map(d => (d.id === docId || d.documentType === docId ? { ...d, ...updatedFields } : d))
      );

      if (selectedCandidateDetails) {
        setSelectedCandidateDetails(prev => {
          if (!prev) return null;
          return {
            ...prev,
            documents: prev.documents.map(d => (d.id === docId || d.documentType === docId ? { ...d, ...updatedFields } : d))
          };
        });
      }

      if (previewDocument && (previewDocument.id === docId || previewDocument.documentType === docId)) {
        setPreviewDocument(prev => ({ ...prev, ...updatedFields }));
      }

      showSuccess('Document successfully verified and approved.');
    } catch (error) {
      showError('Failed to approve document: ' + error.message);
    }
  };

  const handleApproveAllForCandidate = (candDocs) => {
    const ids = candDocs.map(d => d.id);
    const updatedFields = {
      status: 'approved',
      verifiedBy: 'Super Admin',
      verifiedAt: new Date().toISOString(),
      verificationNotes: 'Verified and approved.'
    };

    setDocuments(prev =>
      prev.map(d => (ids.includes(d.id) ? { ...d, ...updatedFields } : d))
    );

    if (selectedCandidateDetails) {
      setSelectedCandidateDetails(prev => {
        if (!prev) return null;
        return {
          ...prev,
          documents: prev.documents.map(d => ({ ...d, ...updatedFields }))
        };
      });
    }

    if (previewDocument && ids.includes(previewDocument.id)) {
      setPreviewDocument(prev => ({ ...prev, ...updatedFields }));
    }

    showSuccess(`All ${candDocs.length} documents verified for candidate.`);
  };

  const handleBulkApprove = () => {
    if (selectedDocIds.length === 0) return;
    const updatedFields = {
      status: 'approved',
      verifiedBy: 'Super Admin',
      verifiedAt: new Date().toISOString(),
      verificationNotes: 'Bulk verified and approved.'
    };

    setDocuments(prev =>
      prev.map(d => (selectedDocIds.includes(d.id) ? { ...d, ...updatedFields } : d))
    );

    if (selectedCandidateDetails) {
      setSelectedCandidateDetails(prev => {
        if (!prev) return null;
        return {
          ...prev,
          documents: prev.documents.map(d => (selectedDocIds.includes(d.id) ? { ...d, ...updatedFields } : d))
        };
      });
    }

    showSuccess(`Bulk approved ${selectedDocIds.length} selected documents.`);
    setSelectedDocIds([]);
  };

  const handleOpenRejectModal = (doc) => {
    setRejectingDoc(doc);
    setRejectionNotes('');
    setShowRejectModal(true);
  };

  const handleSubmitRejection = async () => {
    if (!rejectingDoc) return;
    const reason = rejectionNotes.trim() || 'Document verification rejected due to formatting or mismatch issues.';

    try {
      try {
        await documentVerificationService.verifyDocument(rejectingDoc.id, 'reject', reason);
      } catch (err) {
        console.log('Backend sync note:', err);
      }

      const updatedFields = {
        status: 'rejected',
        verifiedBy: 'Super Admin',
        verifiedAt: new Date().toISOString(),
        verificationNotes: reason
      };

      setDocuments(prev =>
        prev.map(d => (d.id === rejectingDoc.id || d.documentType === rejectingDoc.id ? { ...d, ...updatedFields } : d))
      );

      if (selectedCandidateDetails) {
        setSelectedCandidateDetails(prev => {
          if (!prev) return null;
          return {
            ...prev,
            documents: prev.documents.map(d => (d.id === rejectingDoc.id || d.documentType === rejectingDoc.id ? { ...d, ...updatedFields } : d))
          };
        });
      }

      if (previewDocument && (previewDocument.id === rejectingDoc.id || previewDocument.documentType === rejectingDoc.id)) {
        setPreviewDocument(prev => ({ ...prev, ...updatedFields }));
      }

      showSuccess('Document verification marked as rejected.');
      setShowRejectModal(false);
      setRejectingDoc(null);
    } catch (error) {
      showError('Failed to reject document: ' + error.message);
    }
  };

  const handleDownloadFile = async (doc) => {
    if (!doc) return;
    const isImg = isImageDoc(doc);
    let docTitle = doc.documentName || doc.originalName || (isImg ? 'candidate_document.png' : 'candidate_document.pdf');
    let fileUrl = doc.fileUrl || doc.url || doc.documentUrl;

    if (!fileUrl || fileUrl === '#' || fileUrl === 'null' || fileUrl === 'undefined' || doc.status === 'missing' || doc.verificationStatus === 'missing') {
      showError(`The candidate has not uploaded "${docTitle}" yet. Please request the candidate to upload the file via email or upload it directly.`);
      return;
    }

    const normExt = getNormalizedFileType(doc).toLowerCase();

    // Ensure clean, correct extension on filename matching actual document type
    if (isImg) {
      if (!docTitle.match(/\.(png|jpg|jpeg|webp|gif|svg)$/i)) {
        docTitle = `${docTitle}.${normExt === 'jpg' || normExt === 'jpeg' ? 'jpg' : 'png'}`;
      }
    } else if (!docTitle.match(/\.(pdf|doc|docx|txt|xlsx)$/i)) {
      docTitle = `${docTitle}.pdf`;
    }

    try {
      // 1. Data URLs (Exact original uploaded binary file preserved as Base64) -> Convert to real Blob and trigger browser download
      if (fileUrl && fileUrl.startsWith('data:')) {
        const blob = dataUrlToBlob(fileUrl);
        if (blob) {
          downloadBlobAsFile(blob, docTitle);
          showSuccess(`Downloaded exact candidate file: ${docTitle}`);
          return;
        }
      }

      // 2. Blob URLs -> Download directly
      if (fileUrl && fileUrl.startsWith('blob:')) {
        const a = document.createElement('a');
        a.href = fileUrl;
        a.download = docTitle;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        showSuccess(`Downloaded exact candidate file: ${docTitle}`);
        return;
      }

      // 3. HTTP / Local Uploads (/uploads/...) / S3 URLs -> Use Backend Download Proxy (bypasses CORS and forces browser file download)
      if (fileUrl && fileUrl !== '#' && fileUrl !== 'null' && fileUrl !== 'undefined') {
        const cleanBaseUrl = (API_BASE_URL || '').replace(/\/$/, '');
        const proxyUrl = `${cleanBaseUrl}/document-verification/download-file?url=${encodeURIComponent(fileUrl)}&filename=${encodeURIComponent(docTitle)}`;
        
        try {
          const res = await fetch(proxyUrl);
          if (res.ok) {
            const blob = await res.blob();
            downloadBlobAsFile(blob, docTitle);
            showSuccess(`Downloaded exact candidate file: ${docTitle}`);
            return;
          }
        } catch (proxyErr) {
          console.warn('Backend proxy fetch note, trying direct fetch:', proxyErr.message);
        }

        // Direct fetch fallback if backend proxy fails
        try {
          const res = await fetch(fileUrl);
          if (res.ok) {
            const blob = await res.blob();
            downloadBlobAsFile(blob, docTitle);
            showSuccess(`Downloaded exact candidate file: ${docTitle}`);
            return;
          }
        } catch (directErr) {
          console.warn('Direct fetch note, trying Canvas exporter:', directErr.message);
        }

        // Canvas fallback for images
        if (isImg) {
          const exported = await new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
              try {
                const canvas = document.createElement('canvas');
                canvas.width = img.naturalWidth || img.width || 800;
                canvas.height = img.naturalHeight || img.height || 600;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);
                const mime = normExt === 'jpg' || normExt === 'jpeg' ? 'image/jpeg' : 'image/png';
                canvas.toBlob((b) => {
                  if (b) {
                    downloadBlobAsFile(b, docTitle);
                    resolve(true);
                  } else resolve(false);
                }, mime);
              } catch (e) {
                resolve(false);
              }
            };
            img.onerror = () => resolve(false);
            img.src = fileUrl;
          });
          if (exported) {
            showSuccess(`Downloaded exact candidate file: ${docTitle}`);
            return;
          }
        }

        // Direct anchor download trigger
        const a = document.createElement('a');
        a.href = fileUrl;
        a.download = docTitle;
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        showSuccess(`Downloaded exact candidate file: ${docTitle}`);
        return;
      }

      // 4. Try backend document verification service by ID if document exists in DB
      if (doc.id) {
        const res = await documentVerificationService.downloadDocument(doc.id, { fileName: docTitle });
        if (res.success) {
          showSuccess(`Downloaded exact candidate file: ${docTitle}`);
          return;
        }
      }

      showError(`Unable to download ${docTitle}. The physical file is not available on the server.`);
    } catch (err) {
      showError('Error triggering file download: ' + err.message);
    }
  };

  const handleCreateUpload = (e) => {
    e.preventDefault();
    if (!uploadCandidateId) {
      showError('Please select a candidate for document upload.');
      return;
    }
    if (!uploadDocName) {
      showError('Please enter a document title.');
      return;
    }

    const cand = candidatesList.find(c => (c._id || c.id || c.candidateId) === uploadCandidateId) || {
      firstName: 'Uploaded',
      lastName: 'Candidate',
      email: 'cand@example.com',
      phone: '+91 99999 88888',
      jobTitle: 'Software Engineer',
      department: 'Technology',
      recruitmentStatus: 'documentation'
    };

    const processUploadDoc = (fileDataUrl, fileExt, fileSizeStr) => {
      const newDoc = {
        id: `doc_custom_${Date.now()}`,
        candidateId: uploadCandidateId,
        candidateName: `${cand.firstName || ''} ${cand.lastName || ''}`.trim() || 'Candidate',
        candidateEmail: cand.email || 'cand@example.com',
        candidatePhone: cand.phone || 'N/A',
        jobTitle: cand.jobTitle || 'Position',
        department: cand.department || 'General',
        candidateStage: cand.recruitmentStatus || 'documentation',
        documentName: uploadDocName,
        documentType: uploadCategory,
        categoryLabel: uploadCategory.toUpperCase(),
        fileSize: fileSizeStr || '1.4 MB',
        fileType: fileExt || 'PDF',
        fileUrl: fileDataUrl || '#',
        uploadedAt: new Date().toISOString(),
        status: 'pending',
        verifiedBy: null,
        verifiedAt: null,
        verificationNotes: 'Uploaded by HR Admin. Pending verification.'
      };

      setDocuments(prev => [newDoc, ...prev]);

      if (selectedCandidateDetails && selectedCandidateDetails.candidateId === uploadCandidateId) {
        setSelectedCandidateDetails(prev => ({
          ...prev,
          documents: [newDoc, ...prev.documents]
        }));
      }

      try {
        const existingLocal = JSON.parse(localStorage.getItem('hrms_candidate_uploaded_docs') || '[]');
        localStorage.setItem('hrms_candidate_uploaded_docs', JSON.stringify([newDoc, ...existingLocal]));
      } catch (err) {}

      showSuccess('Candidate document uploaded successfully.');
      setShowUploadModal(false);
      setUploadDocName('');
      setUploadFile(null);
    };

    if (uploadFile) {
      const reader = new FileReader();
      reader.onload = (evt) => {
        const ext = (uploadFile.name.split('.').pop() || 'pdf').toUpperCase();
        const sizeStr = `${(uploadFile.size / 1024 / 1024).toFixed(1)} MB`;
        processUploadDoc(evt.target.result, ext, sizeStr);
      };
      reader.readAsDataURL(uploadFile);
    } else {
      processUploadDoc('#', 'PDF', '1.4 MB');
    }
  };

  // Direct file attachment for specific document requirement in matrix table
  const handleUploadForDoc = (docItem) => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.pdf,.jpg,.jpeg,.png,.webp';
    fileInput.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = async (evt) => {
        const fileUrl = evt.target.result;
        const fileExt = (file.name.split('.').pop() || 'pdf').toUpperCase();

        const cId = docItem.candidateId || (selectedCandidateDetails ? selectedCandidateDetails.candidateId : 'cand_1');
        const cName = docItem.candidateName || (selectedCandidateDetails ? selectedCandidateDetails.candidateName : 'Candidate');
        const cEmail = docItem.candidateEmail || (selectedCandidateDetails ? selectedCandidateDetails.candidateEmail : 'candidate@example.com');
        const dType = docItem.documentType || docItem.id;
        const catLabel = docItem.categoryLabel || docItem.name || 'Master Document';

        const updatedDoc = {
          id: docItem.id || `uploaded_${cId}_${dType}_${Date.now()}`,
          candidateId: cId,
          candidateName: cName,
          candidateEmail: cEmail,
          jobTitle: docItem.jobTitle || 'Software Engineer',
          documentName: file.name,
          documentType: dType,
          categoryLabel: catLabel,
          fileSize: `${(file.size / 1024 / 1024).toFixed(1)} MB`,
          fileType: fileExt,
          fileUrl: fileUrl,
          uploadedAt: new Date().toISOString(),
          status: 'pending',
          verificationStatus: 'pending',
          verifiedBy: null,
          verifiedAt: null,
          verificationNotes: 'Uploaded by HR on behalf of candidate. Pending Verification.'
        };

        setDocuments(prev => {
          const exists = prev.some(d => d.id === updatedDoc.id || d.documentType === dType);
          if (exists) {
            return prev.map(d => (d.id === updatedDoc.id || d.documentType === dType) ? updatedDoc : d);
          }
          return [updatedDoc, ...prev];
        });

        if (selectedCandidateDetails) {
          setSelectedCandidateDetails(prev => {
            const docs = prev.documents || [];
            const existsInDetails = docs.some(d => d.id === updatedDoc.id || d.documentType === dType);
            return {
              ...prev,
              documents: existsInDetails 
                ? docs.map(d => (d.id === updatedDoc.id || d.documentType === dType) ? updatedDoc : d)
                : [updatedDoc, ...docs]
            };
          });
        }

        // Sync to localStorage
        try {
          const existingLocal = JSON.parse(localStorage.getItem('hrms_candidate_uploaded_docs') || '[]');
          const filteredLocal = existingLocal.filter(d => d.id !== updatedDoc.id && d.documentType !== dType);
          localStorage.setItem('hrms_candidate_uploaded_docs', JSON.stringify([updatedDoc, ...filteredLocal]));
        } catch (err) {}

        // Submit to backend
        try {
          await documentVerificationService.submitCandidateDocuments(cId, [{
            documentType: dType,
            documentName: file.name,
            categoryLabel: catLabel,
            fileSize: `${(file.size / 1024 / 1024).toFixed(1)} MB`,
            fileType: fileExt,
            fileUrl: fileUrl
          }]);
        } catch (err) {}

        showSuccess(`Uploaded ${file.name} for ${catLabel}. Status updated to Pending Review.`);
      };
      reader.readAsDataURL(file);
    };
    fileInput.click();
  };

  // Submit Document Request & Create Requested Entries in Candidate's List + Dispatch Real Email to Candidate
  const handleSendRequest = async (e) => {
    e.preventDefault();
    if (!reqCandidateId) {
      showError('Please select a candidate to request documents from.');
      return;
    }

    if (selectedReqDocIds.length === 0) {
      showError('Please select at least one document checkbox to request.');
      return;
    }

    try {
      setIsSendingRequest(true);

      const allAvailableDocs = availableRequestDocuments;
      const requestedDocsToCreate = allAvailableDocs.filter(d => selectedReqDocIds.includes(d.id));

      const targetCand = candidatesList.find(c => (c._id || c.id || c.candidateId) === reqCandidateId) || {
        firstName: 'Candidate',
        lastName: '',
        email: 'candidate@example.com',
        phone: 'N/A',
        jobTitle: 'Software Engineer',
        department: 'Engineering',
        recruitmentStatus: 'documentation'
      };

      const cName = `${targetCand.firstName || ''} ${targetCand.lastName || ''}`.trim() || 'Candidate';
      const cEmail = targetCand.email || 'candidate@example.com';

      const newRequestedEntries = requestedDocsToCreate.map((docItem, idx) => ({
        id: `req_doc_${Date.now()}_${idx}`,
        candidateId: reqCandidateId,
        candidateName: cName,
        candidateEmail: cEmail,
        candidatePhone: targetCand.phone || 'N/A',
        jobTitle: targetCand.jobTitle || 'Software Engineer',
        department: targetCand.department || 'Engineering',
        candidateStage: targetCand.recruitmentStatus || 'documentation',
        documentName: `${docItem.name.replace(/\s+/g, '_')}_Pending.pdf`,
        documentType: docItem.id || 'other',
        categoryLabel: docItem.categoryLabel || 'Master Configured Document',
        fileSize: 'Pending Upload',
        fileType: 'PDF',
        fileUrl: '#',
        uploadedAt: new Date().toISOString(),
        status: 'missing',
        verifiedBy: null,
        verifiedAt: null,
        verificationNotes: `Requested by HR on ${new Date().toLocaleDateString('en-IN')}. Upload due by ${reqDueDate || '5 days'}.${reqNote ? ` Note: ${reqNote}` : ''}`
      }));

      // Call real backend API to record request and dispatch email to Candidate's inbox!
      const emailRes = await documentVerificationService.sendRequestEmail({
        candidateId: reqCandidateId,
        candidateName: cName,
        candidateEmail: cEmail,
        jobTitle: targetCand.jobTitle || 'Software Engineer',
        requestedDocuments: requestedDocsToCreate,
        dueDate: reqDueDate,
        note: reqNote
      });

      // Update state locally first so UI is instantly responsive
      setDocuments(prev => [...newRequestedEntries, ...prev]);

      if (selectedCandidateDetails && selectedCandidateDetails.candidateId === reqCandidateId) {
        setSelectedCandidateDetails(prev => ({
          ...prev,
          documents: [...newRequestedEntries, ...prev.documents]
        }));
      }

      if (emailRes.success) {
        showSuccess(`Document request email successfully sent to ${cName} (${cEmail}) for ${requestedDocsToCreate.length} document(s).`);
      } else {
        showSuccess(`Document request recorded for ${cName}. (${emailRes.error || 'Saved'})`);
      }

      setShowRequestModal(false);
    } catch (err) {
      showError(`Failed to send document request: ${err.message || 'Error occurred'}`);
    } finally {
      setIsSendingRequest(false);
    }
  };

  // Helper Badge Renderers
  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 shadow-sm">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Verified
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200 shadow-sm">
            <XCircle className="w-3.5 h-3.5 text-rose-600" /> Rejected
          </span>
        );
      case 'missing':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200 shadow-sm">
            <AlertCircle className="w-3.5 h-3.5 text-gray-500" /> Requested / Pending Upload
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 shadow-sm">
            <Clock className="w-3.5 h-3.5 text-amber-600" /> Pending Review
          </span>
        );
    }
  };

  const getStageBadge = (stage) => {
    const stages = {
      applied: 'bg-blue-50 text-blue-700 border-blue-200',
      shortlisted: 'bg-indigo-50 text-indigo-700 border-indigo-200',
      interviewed: 'bg-purple-50 text-purple-700 border-purple-200',
      documentation: 'bg-teal-50 text-teal-700 border-teal-200',
      offered: 'bg-cyan-50 text-cyan-700 border-cyan-200',
      hired: 'bg-emerald-50 text-emerald-700 border-emerald-200'
    };
    const cls = stages[stage] || 'bg-gray-50 text-gray-700 border-gray-200';
    return (
      <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium border uppercase tracking-wider ${cls}`}>
        {stage}
      </span>
    );
  };

  // RENDER DEDICATED FULL CANDIDATE DOCUMENTATION DETAILS PAGE VIEW (WHEN A CANDIDATE IS SELECTED)
  if (selectedCandidateDetails) {
    const candDocs = selectedCandidateDetails.documents;
    const totalCandDocs = candDocs.length;
    const verifiedCount = candDocs.filter(d => d.status === 'approved').length;
    const pendingCount = candDocs.filter(d => d.status === 'pending').length;
    const rejectedCount = candDocs.filter(d => d.status === 'rejected').length;
    const missingCount = candDocs.filter(d => d.status === 'missing').length;
    const percent = totalCandDocs > 0 ? Math.round((verifiedCount / totalCandDocs) * 100) : 0;

    return (
      <div className="space-y-6 pb-12 animate-in fade-in duration-200">
        <CustomAlert
          isOpen={alertState.isOpen}
          message={alertState.message}
          type={alertState.type}
          onClose={hideAlert}
        />

        {/* Top Back Navigation Bar & Quick Actions */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3">
            <button
              onClick={handleCloseCandidateDossier}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs transition-all shadow-sm"
              title="Return to Candidates List"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Candidate List</span>
            </button>

            <div className="text-xs text-gray-400 font-medium hidden md:block">
              <span>Recruitment</span> / <span>Documentation</span> / <span className="text-gray-900 font-bold">{selectedCandidateDetails.candidateName}</span>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => {
                setReqCandidateId(selectedCandidateDetails.candidateId);
                setShowRequestModal(true);
              }}
              className="px-4 py-2.5 rounded-xl bg-gray-50 hover:bg-gray-100 text-gray-800 font-bold text-xs transition-all border border-gray-200 flex items-center gap-1.5"
            >
              <Send className="w-4 h-4 text-gray-600" /> Request Upload
            </button>

            <button
              onClick={() => {
                setUploadCandidateId(selectedCandidateDetails.candidateId);
                setShowUploadModal(true);
              }}
              className="px-4 py-2.5 rounded-xl bg-brand text-white font-bold text-xs hover:bg-brand-600 transition-all shadow-sm flex items-center gap-1.5"
            >
              <Plus className="w-4 h-4" /> Upload Document
            </button>

            {pendingCount > 0 && (
              <button
                onClick={() => handleApproveAllForCandidate(candDocs)}
                className="px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs transition-all shadow-sm flex items-center gap-1.5"
              >
                <CheckCircle2 className="w-4 h-4" /> Approve All Pending
              </button>
            )}
          </div>
        </div>

        {/* Candidate Profile Summary Banner */}
        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-brand-100 text-brand font-black text-2xl flex items-center justify-center border border-brand-200 shadow-inner">
              {selectedCandidateDetails.candidateName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-xl md:text-2xl font-extrabold text-gray-900">{selectedCandidateDetails.candidateName}</h1>
                {getStageBadge(selectedCandidateDetails.candidateStage)}
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-600 flex-wrap">
                <span className="flex items-center gap-1 font-semibold text-gray-800">
                  <Briefcase className="w-3.5 h-3.5 text-gray-400" /> {selectedCandidateDetails.jobTitle}
                </span>
                <span>•</span>
                <span className="flex items-center gap-1 text-gray-600">
                  <Mail className="w-3.5 h-3.5 text-gray-400" /> {selectedCandidateDetails.candidateEmail}
                </span>
                <span>•</span>
                <span className="flex items-center gap-1 text-gray-600">
                  <Phone className="w-3.5 h-3.5 text-gray-400" /> {selectedCandidateDetails.candidatePhone}
                </span>
              </div>
            </div>
          </div>

          {/* Verification Score & Audit Stats */}
          <div className="bg-slate-50 p-4 rounded-2xl border border-gray-200 flex flex-wrap items-center gap-6 text-xs">
            <div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Total Uploaded</span>
              <p className="font-extrabold text-base text-gray-900">{totalCandDocs} Files</p>
            </div>
            <div className="h-8 w-px bg-gray-200 hidden sm:block" />
            <div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600">Verified</span>
              <p className="font-extrabold text-base text-emerald-600">{verifiedCount} Files</p>
            </div>
            <div className="h-8 w-px bg-gray-200 hidden sm:block" />
            <div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-amber-600">Pending Review</span>
              <p className="font-extrabold text-base text-amber-600">{pendingCount} Files</p>
            </div>
            <div className="h-8 w-px bg-gray-200 hidden sm:block" />
            <div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-rose-600">Rejected</span>
              <p className="font-extrabold text-base text-rose-600">{rejectedCount} Files</p>
            </div>
            <div className="h-8 w-px bg-gray-200 hidden sm:block" />
            <div className="min-w-36">
              <div className="flex items-center justify-between text-xs">
                <span className="font-bold text-gray-700">Audit Score</span>
                <span className="font-black text-brand">{percent}%</span>
              </div>
              <div className="w-full bg-gray-200 h-2 rounded-full mt-1 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${percent === 100 ? 'bg-emerald-500' : 'bg-brand'}`}
                  style={{ width: `${percent}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Structured Document Verification Matrix Table */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden p-6 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-gray-100 pb-3">
            <div>
              <h2 className="font-extrabold text-lg text-gray-900 flex items-center gap-2">
                <FileCheck className="w-5 h-5 text-brand" />
                <span>Candidate Document Verification Matrix & Details</span>
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Complete audit checklist of identity proof, educational degrees, experience certificates & financial documents.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleRefresh}
                className="px-3 py-1.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold transition-all flex items-center gap-1.5"
                title="Sync Real-time Upload Status"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
                <span>Sync Real-Time</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setUploadCandidateId(selectedCandidateDetails.candidateId);
                  setShowUploadModal(true);
                }}
                className="px-3.5 py-1.5 rounded-xl bg-brand hover:bg-brand-600 text-white text-xs font-bold shadow-md transition-all flex items-center gap-1.5"
              >
                <Upload className="w-3.5 h-3.5" />
                <span>Upload Document</span>
              </button>
            </div>
          </div>

          <div className="overflow-x-auto border border-gray-200 rounded-xl">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-[#edf4ff] border-b border-blue-100 text-[11px] font-extrabold text-slate-700 uppercase tracking-wider">
                  <th className="py-3.5 px-4">Document Type / Requirement <span className="text-slate-400 font-normal ml-0.5">↑↓</span></th>
                  <th className="py-3.5 px-4">Uploaded File</th>
                  <th className="py-3.5 px-4">Format & Size</th>
                  <th className="py-3.5 px-4">Upload Timestamp <span className="text-slate-400 font-normal ml-0.5">↑↓</span></th>
                  <th className="py-3.5 px-4">Status <span className="text-slate-400 font-normal ml-0.5">↑↓</span></th>
                  <th className="py-3.5 px-4">Verification Audit</th>
                  <th className="py-3.5 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-xs">
                {availableRequestDocuments.map(stdDoc => {
                  const matchedDoc = selectedCandidateDetails.documents.find(d =>
                    d.documentType === stdDoc.id ||
                    d.documentName.toLowerCase().includes(stdDoc.id.replace('_', ' ')) ||
                    (stdDoc.id === 'pan_card' && d.documentType === 'identity') ||
                    (stdDoc.id === 'degree_certificate' && d.documentType === 'education') ||
                    (stdDoc.id === 'experience_letter' && d.documentType === 'experience')
                  );

                  return (
                    <tr key={stdDoc.id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-3.5 px-4 font-extrabold text-gray-900">
                        <div className="flex items-center gap-2">
                          <div className={`p-1.5 rounded-lg ${matchedDoc ? 'bg-brand-50 text-brand' : 'bg-gray-100 text-gray-400'}`}>
                            <FileText className="w-4 h-4" />
                          </div>
                          <div>
                            <p>{stdDoc.name}</p>
                            <span className="text-[10px] text-gray-400 font-normal">
                              {stdDoc.category === 'master' ? 'Master Configured Requirement' : 'Standard Requirement'}
                            </span>
                          </div>
                        </div>
                      </td>

                      <td className="py-3.5 px-4">
                        {matchedDoc ? (
                          <div
                            onClick={() => setPreviewDocument(matchedDoc)}
                            className="font-bold text-gray-900 hover:text-brand transition-colors cursor-pointer flex items-center gap-1.5"
                          >
                            <FileText className="w-3.5 h-3.5 text-brand" />
                            <span>{matchedDoc.documentName}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400 italic">Not Uploaded Yet</span>
                        )}
                      </td>

                      <td className="py-3.5 px-4 font-medium text-gray-600">
                        {matchedDoc ? `${getNormalizedFileType(matchedDoc)} • ${matchedDoc.fileSize}` : '-'}
                      </td>

                      <td className="py-3.5 px-4 text-gray-600">
                        {matchedDoc ? formatDisplayDate(matchedDoc.uploadedAt) : '-'}
                      </td>

                      <td className="py-3.5 px-4">
                        {matchedDoc ? getStatusBadge(matchedDoc.status) : (
                          <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-gray-100 text-gray-500 border border-gray-200">
                            Missing / Pending
                          </span>
                        )}
                      </td>

                      <td className="py-3.5 px-4 text-gray-600">
                        {matchedDoc ? (
                          <div>
                            <p className="font-semibold text-gray-800">{matchedDoc.verifiedBy || 'HR Manager'}</p>
                            {matchedDoc.verificationNotes && (
                              <p className="text-[10px] text-gray-500 truncate max-w-[150px]">{matchedDoc.verificationNotes}</p>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400 font-normal">Pending candidate submission</span>
                        )}
                      </td>

                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() => handleUploadForDoc(matchedDoc || { ...stdDoc, candidateId: selectedCandidateDetails.candidateId, candidateName: selectedCandidateDetails.candidateName, candidateEmail: selectedCandidateDetails.candidateEmail })}
                            className="px-2.5 py-1 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-extrabold flex items-center gap-1 shadow-sm transition-all"
                            title="Upload Document File"
                          >
                            <Upload className="w-3 h-3" />
                            <span>{matchedDoc && matchedDoc.status !== 'missing' ? 'Replace File' : 'Upload File'}</span>
                          </button>

                          {matchedDoc && matchedDoc.status !== 'missing' && (
                            <>
                              <button
                                onClick={() => setPreviewDocument(matchedDoc)}
                                className="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
                                title="Preview Document"
                              >
                                <Eye className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => handleDownloadFile(matchedDoc)}
                                className="p-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 transition-colors"
                                title="Download File"
                              >
                                <Download className="w-3.5 h-3.5" />
                              </button>

                              {matchedDoc.status !== 'approved' && (
                                <button
                                  onClick={() => handleApproveDocument(matchedDoc.id)}
                                  className="p-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 transition-colors"
                                  title="Approve Document"
                                >
                                  <CheckCircle2 className="w-3.5 h-3.5" />
                                </button>
                              )}

                              {matchedDoc.status !== 'rejected' && (
                                <button
                                  onClick={() => handleOpenRejectModal(matchedDoc)}
                                  className="p-1.5 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-700 transition-colors"
                                  title="Reject Document"
                                >
                                  <XCircle className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {/* Additional Custom Uploads */}
                {selectedCandidateDetails.documents
                  .filter(d => !availableRequestDocuments.some(std => std.id === d.documentType))
                  .map(extraDoc => (
                    <tr key={extraDoc.id} className="hover:bg-blue-50/20 transition-colors bg-slate-50/40">
                      <td className="py-3.5 px-4 font-extrabold text-gray-900">
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 rounded-lg bg-purple-50 text-purple-600">
                            <FileText className="w-4 h-4" />
                          </div>
                          <div>
                            <p>{extraDoc.documentName}</p>
                            <span className="text-[10px] text-purple-600 font-semibold">Custom Upload</span>
                          </div>
                        </div>
                      </td>

                      <td className="py-3.5 px-4 font-bold text-gray-900">
                        <div className="flex items-center gap-1.5 cursor-pointer hover:text-brand" onClick={() => setPreviewDocument(extraDoc)}>
                          <span>{extraDoc.documentName}</span>
                          <ExternalLink className="w-3.5 h-3.5 text-brand shrink-0" />
                        </div>
                      </td>

                      <td className="py-3.5 px-4 text-gray-600">
                        <span className="font-semibold">{getNormalizedFileType(extraDoc)}</span> • {extraDoc.fileSize}
                      </td>

                      <td className="py-3.5 px-4 text-gray-600">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5 text-gray-400" />
                          <span>{formatDisplayDate(extraDoc.uploadedAt)}</span>
                        </div>
                      </td>

                      <td className="py-3.5 px-4">
                        {getStatusBadge(extraDoc.status)}
                      </td>

                      <td className="py-3.5 px-4 text-gray-600">
                        {extraDoc.verifiedBy ? (
                          <div>
                            <p className="font-semibold text-emerald-800">Verified by {extraDoc.verifiedBy}</p>
                            {extraDoc.verificationNotes && <p className="text-[10px] text-gray-500 truncate max-w-xs">{extraDoc.verificationNotes}</p>}
                          </div>
                        ) : (
                          <p className="text-gray-400 italic">Unverified</p>
                        )}
                      </td>

                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setPreviewDocument(extraDoc)}
                            className="p-1.5 rounded-lg text-gray-600 hover:text-brand hover:bg-brand-50 transition-all"
                            title="Preview Document"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDownloadFile(extraDoc)}
                            className="p-1.5 rounded-lg text-gray-600 hover:text-brand hover:bg-brand-50 transition-all"
                            title="Download Document"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* DOCUMENT DETAILS & DOWNLOAD MODAL */}
        {previewDocument && (
          <div className="fixed inset-0 z-50 bg-transparent flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 border border-gray-200">
              {/* Modal Top Header Bar */}
              <div className="p-5 bg-slate-900 text-white flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-xl bg-white/10 text-brand-300 border border-white/10">
                    <FileText className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded bg-brand-500/20 text-brand-300 font-extrabold text-[10px] uppercase tracking-wider border border-brand-400/20">
                        {getNormalizedFileType(previewDocument)} FILE
                      </span>
                      <h3 className="font-extrabold text-base text-white truncate max-w-md">{previewDocument.documentName}</h3>
                    </div>
                    <p className="text-xs text-slate-300 mt-0.5">
                      Candidate: <span className="font-bold text-white">{previewDocument.candidateName}</span> ({previewDocument.jobTitle || 'Candidate'})
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  {getStatusBadge(previewDocument.status)}
                  <button
                    onClick={() => setPreviewDocument(null)}
                    className="p-2 rounded-xl hover:bg-white/10 text-gray-300 hover:text-white transition-all"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Modal Main Content Area */}
              <div className="flex-1 overflow-y-auto p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                
                {/* Left Panel: Document Download & Media Hub */}
                <div className="bg-white rounded-2xl border border-gray-200 p-5 flex flex-col justify-between space-y-4 text-left min-h-[320px]">
                  {isImageDoc(previewDocument) ? (
                    <div className="w-full space-y-4 flex flex-col items-center">
                      <div className="bg-slate-900 p-3 rounded-2xl border border-slate-700 shadow-inner max-w-full overflow-hidden flex items-center justify-center">
                        <img
                          src={previewDocument.fileUrl || previewDocument.url}
                          alt={previewDocument.documentName || "Document Image Preview"}
                          className="max-h-[220px] max-w-full object-contain rounded-xl shadow-lg border border-slate-700/50"
                        />
                      </div>
                      <div className="flex items-center gap-2 flex-wrap justify-center w-full">
                        <button
                          onClick={() => handleDownloadFile(previewDocument)}
                          className="w-full py-3 rounded-xl bg-brand text-white font-extrabold text-xs shadow-md hover:bg-brand-600 transition-all flex items-center justify-center gap-2"
                        >
                          <Download className="w-4 h-4" /> Download Image File ({getNormalizedFileType(previewDocument)})
                        </button>
                        {(previewDocument.fileUrl || previewDocument.url || activePdfBlobUrl) && (
                          <a
                            href={(previewDocument.fileUrl && previewDocument.fileUrl !== '#') ? (previewDocument.fileUrl || previewDocument.url) : activePdfBlobUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs shadow-sm transition-all flex items-center justify-center gap-1.5"
                          >
                            <ExternalLink className="w-4 h-4" /> Open Full Image in New Tab
                          </a>
                        )}
                      </div>
                    </div>
                  ) : (
                    /* Executive Real Document Viewer / Embedded PDF & Download Specification Card */
                    <div className="w-full flex-1 flex flex-col justify-between space-y-4">
                      <div className="space-y-3">
                        <div className="flex items-center gap-3 border-b border-gray-100 pb-3">
                          <div className="w-12 h-12 rounded-xl bg-blue-50 text-brand font-black flex items-center justify-center border border-blue-100 shadow-inner flex-shrink-0">
                            <FileText className="w-6 h-6" />
                          </div>
                          <div className="min-w-0">
                            <span className="px-2 py-0.5 rounded bg-blue-100 text-brand font-extrabold text-[10px] uppercase tracking-wider">
                              {getNormalizedFileType(previewDocument)} DOCUMENT
                            </span>
                            <h4 className="font-extrabold text-sm text-gray-900 truncate mt-0.5" title={previewDocument.documentName}>
                              {previewDocument.documentName}
                            </h4>
                          </div>
                        </div>

                        {/* If actual uploaded file exists, render embedded document frame */}
                        {previewDocument.fileUrl && previewDocument.fileUrl !== '#' && previewDocument.fileUrl !== 'null' && previewDocument.status !== 'missing' ? (
                          <div className="rounded-xl overflow-hidden border border-gray-200 bg-slate-900/5 shadow-inner">
                            <iframe
                              src={previewDocument.fileUrl}
                              title={previewDocument.documentName}
                              className="w-full h-[220px] rounded-xl bg-white border-0"
                            />
                          </div>
                        ) : (
                          /* Document Pending Submission Specification Card */
                          <div className="bg-white p-3 rounded-xl border border-gray-200 space-y-2 text-xs">
                            <div className="flex items-center justify-between">
                              <span className="font-semibold text-gray-500">Requirement Category:</span>
                              <span className="font-bold text-gray-800">{previewDocument.categoryLabel || 'Verification Requirement'}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="font-semibold text-gray-500">File Extension:</span>
                              <span className="font-bold text-gray-800">.{getNormalizedFileType(previewDocument).toLowerCase()}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="font-semibold text-gray-500">File Size:</span>
                              <span className="font-bold text-gray-800">{previewDocument.fileSize || 'Standard Document'}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="font-semibold text-gray-500">Candidate Owner:</span>
                              <span className="font-bold text-gray-900">{previewDocument.candidateName || 'Candidate'}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="font-semibold text-gray-500">Upload Date:</span>
                              <span className="font-bold text-gray-800">{formatDisplayDate(previewDocument.uploadedAt)}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="font-semibold text-gray-500">Submission Status:</span>
                              <span className="font-extrabold text-amber-600 flex items-center gap-1">
                                <Clock className="w-3.5 h-3.5" /> Pending Candidate Upload
                              </span>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="w-full space-y-2 pt-2">
                        {previewDocument.fileUrl && previewDocument.fileUrl !== '#' && previewDocument.status !== 'missing' ? (
                          <>
                            <button
                              onClick={() => handleDownloadFile(previewDocument)}
                              className="w-full py-3 rounded-xl bg-brand text-white font-extrabold text-xs shadow-md hover:bg-brand-600 transition-all flex items-center justify-center gap-2"
                            >
                              <Download className="w-4 h-4" /> Download Exact Uploaded File ({getNormalizedFileType(previewDocument)})
                            </button>
                            
                            <a
                              href={previewDocument.fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-full py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold text-xs transition-all flex items-center justify-center gap-1.5"
                            >
                              <ExternalLink className="w-3.5 h-3.5" /> Open Exact Document in New Tab
                            </a>
                          </>
                        ) : (
                          <div className="space-y-2">
                            <p className="text-[11px] text-amber-700 bg-amber-50 p-2 rounded-xl border border-amber-200 text-center font-medium">
                              Candidate has not uploaded this document yet.
                            </p>
                            <button
                              type="button"
                              onClick={() => {
                                setPreviewDocument(null);
                                setReqCandidateId(previewDocument.candidateId);
                                setShowRequestModal(true);
                              }}
                              className="w-full py-2.5 rounded-xl bg-brand text-white font-bold text-xs shadow-sm hover:bg-brand-600 transition-all flex items-center justify-center gap-1.5"
                            >
                              <Send className="w-3.5 h-3.5" /> Request Upload from Candidate
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Right Panel: Complete Candidate & Verification Audit Details */}
                <div className="space-y-5 text-xs flex flex-col justify-between">
                  
                  {/* Section 1: Candidate Profile Details */}
                  <div className="bg-white p-4 rounded-2xl border border-gray-200 space-y-2">
                    <h4 className="text-[11px] font-bold uppercase tracking-wider text-gray-400">Candidate Information</h4>
                    <div className="space-y-1.5 text-gray-700">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-gray-500">Full Name:</span>
                        <span className="font-extrabold text-gray-900 text-sm">{previewDocument.candidateName || 'N/A'}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-gray-500">Email Address:</span>
                        <span className="font-bold text-gray-800">{previewDocument.candidateEmail || 'N/A'}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-gray-500">Phone Number:</span>
                        <span className="font-bold text-gray-800">{previewDocument.candidatePhone || 'N/A'}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-gray-500">Job Position:</span>
                        <span className="font-bold text-gray-800">{previewDocument.jobTitle || 'N/A'}</span>
                      </div>
                      <div className="flex items-center justify-between pt-1">
                        <span className="font-semibold text-gray-500">Recruitment Stage:</span>
                        {getStageBadge(previewDocument.candidateStage)}
                      </div>
                    </div>
                  </div>

                  {/* Section 2: Verification Audit Trail Details */}
                  <div className="bg-white p-4 rounded-2xl border border-gray-200 space-y-2">
                    <h4 className="text-[11px] font-bold uppercase tracking-wider text-blue-600">Verification Audit Trail</h4>
                    <div className="space-y-1.5 text-gray-700">
                      <div className="flex items-start justify-between gap-2">
                        <span className="font-semibold text-gray-500 flex-shrink-0">Document Type:</span>
                        <span className="font-extrabold text-gray-900 text-right break-all max-w-[200px]">{previewDocument.documentName}</span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-gray-500 flex-shrink-0">File Type / Format:</span>
                        <span className="font-bold text-gray-900">{getNormalizedFileType(previewDocument)} ({previewDocument.fileSize || 'Standard'})</span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-gray-500 flex-shrink-0">Upload Date & Time:</span>
                        <span className="font-bold text-gray-800">{formatDisplayDate(previewDocument.uploadedAt)}</span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-gray-500 flex-shrink-0">Audit Status:</span>
                        {getStatusBadge(previewDocument.status)}
                      </div>
                      {previewDocument.verifiedBy && (
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold text-gray-500 flex-shrink-0">Verified By:</span>
                          <span className="font-bold text-emerald-700">{previewDocument.verifiedBy}</span>
                        </div>
                      )}
                      {previewDocument.verificationNotes && (
                        <div className="pt-1 border-t border-gray-100">
                          <span className="font-semibold text-gray-500 block mb-0.5">Audit Note:</span>
                          <p className="text-gray-700 italic bg-gray-50 p-2 rounded-lg border border-gray-200">{previewDocument.verificationNotes}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Section 3: Verification Actions */}
                  <div className="pt-2 space-y-2">
                    <h4 className="text-[11px] font-bold uppercase tracking-wider text-gray-400">Verification Actions</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {previewDocument.status !== 'approved' && (
                        <button
                          onClick={() => handleApproveDocument(previewDocument.id)}
                          className="w-full py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs transition-all shadow-sm flex items-center justify-center gap-1.5"
                        >
                          <CheckCircle2 className="w-4 h-4" /> Approve & Verify
                        </button>
                      )}
                      {previewDocument.status !== 'rejected' && (
                        <button
                          onClick={() => handleOpenRejectModal(previewDocument)}
                          className="w-full py-2.5 rounded-xl bg-rose-50 text-rose-700 hover:bg-rose-100 font-bold text-xs transition-all border border-rose-200 flex items-center justify-center gap-1.5"
                        >
                          <XCircle className="w-4 h-4" /> Reject Document
                        </button>
                      )}
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        )}

        {/* UPLOAD DOCUMENT MODAL */}
        {showUploadModal && (
          <div className="fixed inset-0 z-50 bg-transparent flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl p-6 space-y-5 animate-in fade-in zoom-in-95 duration-200">
              <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                <div className="flex items-center gap-2">
                  <Upload className="w-5 h-5 text-brand" />
                  <h3 className="font-bold text-base text-gray-900">Upload Candidate Document</h3>
                </div>
                <button onClick={() => setShowUploadModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleCreateUpload} className="space-y-4 text-xs">
                <div>
                  <label className="block font-bold text-gray-700 mb-1">Select Candidate (Completed Interviews Only) *</label>
                  <select
                    value={uploadCandidateId}
                    onChange={(e) => setUploadCandidateId(e.target.value)}
                    className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-brand/20"
                    required
                  >
                    <option value="">-- Choose Candidate with Completed Interview --</option>
                    {completedInterviewCandidates.length > 0 ? (
                      completedInterviewCandidates.map(c => (
                        <option key={c._id || c.id || c.candidateId} value={c._id || c.id || c.candidateId}>
                          {c.firstName} {c.lastName} ({c.jobTitle || 'Candidate'}) [Interview Completed]
                        </option>
                      ))
                    ) : (
                      <>
                        <option value="cand_1">Salman Khan (Software Engineer) [Interview Completed]</option>
                        <option value="cand_2">Dummy Candidate (UI/UX Designer) [Interview Completed]</option>
                        <option value="cand_3">Prince Kumar (Full Stack Developer) [Interview Completed]</option>
                      </>
                    )}
                  </select>
                </div>

                <div>
                  <label className="block font-bold text-gray-700 mb-1">Document Category *</label>
                  <select
                    value={uploadCategory}
                    onChange={(e) => setUploadCategory(e.target.value)}
                    className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-brand/20"
                  >
                    <option value="identity">Identity Proof (PAN / Aadhar / Passport)</option>
                    <option value="education">Degree & Academic Certificates</option>
                    <option value="experience">Experience / Relieving Letters</option>
                    <option value="offer">Offer Letter & Contract</option>
                    <option value="bank">Bank & Salary Documents</option>
                    <option value="resume">Resume / CV</option>
                    <option value="other">Other Document</option>
                  </select>
                </div>

                <div>
                  <label className="block font-bold text-gray-700 mb-1">Document Name / Title *</label>
                  <input
                    type="text"
                    value={uploadDocName}
                    onChange={(e) => setUploadDocName(e.target.value)}
                    placeholder="e.g. PAN_Card_Verified.pdf"
                    className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-brand/20"
                    required
                  />
                </div>

                <div>
                  <label className="block font-bold text-gray-700 mb-1">Choose File</label>
                  <input
                    type="file"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                    className="w-full p-2 bg-gray-50 border border-gray-200 rounded-xl text-xs"
                  />
                </div>

                <div className="pt-3 border-t border-gray-100 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setShowUploadModal(false)}
                    className="px-4 py-2 rounded-xl bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2 rounded-xl bg-brand text-white font-bold hover:bg-brand-600 shadow-md"
                  >
                    Upload & Save
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* REJECT REASON MODAL */}
        {showRejectModal && rejectingDoc && (
          <div className="fixed inset-0 z-50 bg-transparent flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl p-6 space-y-4 animate-in fade-in zoom-in-95 duration-200">
              <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                <div className="flex items-center gap-2 text-rose-600">
                  <XCircle className="w-5 h-5" />
                  <h3 className="font-bold text-base text-gray-900">Reject Document Verification</h3>
                </div>
                <button onClick={() => setShowRejectModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-3 text-xs">
                <p className="text-gray-600">
                  Rejecting <span className="font-bold text-gray-900">{rejectingDoc.documentName}</span> uploaded by <span className="font-bold text-gray-900">{rejectingDoc.candidateName}</span>.
                </p>

                <div>
                  <label className="block font-bold text-gray-700 mb-1">Rejection Reason & Feedback Notes *</label>
                  <textarea
                    rows={3}
                    value={rejectionNotes}
                    onChange={(e) => setRejectionNotes(e.target.value)}
                    placeholder="Provide feedback on why this document is rejected..."
                    className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-rose-500/20"
                  />
                </div>

                <div className="pt-3 border-t border-gray-100 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setShowRejectModal(false)}
                    className="px-4 py-2 rounded-xl bg-gray-100 text-gray-700 font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmitRejection}
                    className="px-5 py-2 rounded-xl bg-rose-600 text-white font-bold hover:bg-rose-700 shadow-md"
                  >
                    Confirm Rejection
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ENHANCED REQUEST DOCUMENTS FROM CANDIDATE MODAL */}
        {showRequestModal && (
          <div className="fixed inset-0 z-50 bg-transparent flex items-center justify-center p-4">
            <div className="bg-white border border-gray-200 rounded-3xl w-full max-w-2xl shadow-2xl p-6 space-y-5 animate-in fade-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col overflow-hidden">
              <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                <div className="flex items-center gap-2.5 text-brand">
                  <div className="p-2 rounded-xl bg-brand-50 text-brand">
                    <Send className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-extrabold text-base text-gray-900">Request Documents from Candidate</h3>
                    <p className="text-xs text-gray-500">Select candidate and check required documents to request via email</p>
                  </div>
                </div>
                <button onClick={() => setShowRequestModal(false)} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSendRequest} className="space-y-4 text-xs overflow-y-auto flex-1 pr-1">
                <div>
                  <label className="block font-bold text-gray-800 mb-1">
                    Select Target Candidate (Only Candidates with Completed Interviews) *
                  </label>
                  <select
                    value={reqCandidateId}
                    onChange={(e) => setReqCandidateId(e.target.value)}
                    className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-brand/20"
                    required
                  >
                    <option value="">-- Choose Candidate with Completed Interview --</option>
                    {completedInterviewCandidates.length > 0 ? (
                      completedInterviewCandidates.map(c => (
                        <option key={c._id || c.id || c.candidateId} value={c._id || c.id || c.candidateId}>
                          {c.firstName} {c.lastName} ({c.email || 'No email'}) - {c.jobTitle || 'Candidate'} [Interview Completed]
                        </option>
                      ))
                    ) : (
                      candidatesList.map(c => (
                        <option key={c._id || c.id || c.candidateId} value={c._id || c.id || c.candidateId}>
                          {c.firstName} {c.lastName} ({c.email || 'No email'}) - {c.jobTitle || 'Candidate'}
                        </option>
                      ))
                    )}
                  </select>
                  {completedInterviewCandidates.length === 0 && candidatesList.length > 0 && (
                    <p className="text-[11px] text-amber-600 font-semibold mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3.5 h-3.5" />
                      No candidates currently have completed interviews. Complete an interview first to request documents.
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="block font-bold text-gray-800">
                      Select Documents to Request ({selectedReqDocIds.length} Selected) *
                    </label>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleSelectAllReqDocs}
                        className="text-xs text-brand font-bold hover:underline"
                      >
                        Select All Master Documents
                      </button>
                      <span className="text-gray-300">|</span>
                      <button
                        type="button"
                        onClick={handleClearAllReqDocs}
                        className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
                      >
                        Clear All
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 bg-slate-50/80 p-3.5 rounded-2xl border border-gray-200 max-h-56 overflow-y-auto">
                    {availableRequestDocuments.map(doc => {
                      const isChecked = selectedReqDocIds.includes(doc.id);
                      return (
                        <label
                          key={doc.id}
                          className={`flex items-start gap-2.5 p-2.5 rounded-xl border transition-all cursor-pointer ${isChecked ? 'bg-brand-50/70 border-brand-200 text-brand-900 shadow-sm' : 'bg-white border-gray-200 hover:border-gray-300 text-gray-700'}`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleReqDocCheckbox(doc.id)}
                            className="mt-0.5 rounded border-gray-300 text-brand focus:ring-brand"
                          />
                          <div className="min-w-0">
                            <div className="font-bold text-xs leading-snug flex items-center gap-1.5">
                              <span>{doc.name}</span>
                              {doc.category === 'master' && (
                                <span className="px-1.5 py-0.5 rounded text-[9px] font-extrabold bg-blue-100 text-blue-700 uppercase">
                                  Master
                                </span>
                              )}
                            </div>
                            <span className="text-[10px] text-gray-500 font-medium block mt-0.5">{doc.categoryLabel}</span>
                          </div>
                        </label>
                      );
                    })}

                    {customReqDocsList.map(cDoc => {
                      const isChecked = selectedReqDocIds.includes(cDoc.id);
                      return (
                        <label
                          key={cDoc.id}
                          className={`flex items-start gap-2.5 p-2.5 rounded-xl border transition-all cursor-pointer ${isChecked ? 'bg-purple-50 border-purple-200 text-purple-900 shadow-sm' : 'bg-white border-gray-200 text-gray-700'}`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleReqDocCheckbox(cDoc.id)}
                            className="mt-0.5 rounded border-purple-400 text-purple-600 focus:ring-purple-500"
                          />
                          <div className="min-w-0">
                            <p className="font-bold text-xs leading-snug">{cDoc.name}</p>
                            <span className="text-[10px] text-purple-600 font-semibold block mt-0.5">Custom Requirement</span>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={customReqDocName}
                    onChange={(e) => setCustomReqDocName(e.target.value)}
                    placeholder="Add custom document requirement..."
                    className="flex-1 p-2 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-brand/20"
                  />
                  <button
                    type="button"
                    onClick={handleAddCustomReqDoc}
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold text-xs rounded-xl transition-all"
                  >
                    + Add Custom
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block font-bold text-gray-800 mb-1">Submission Deadline / Due Date</label>
                    <input
                      type="date"
                      value={reqDueDate}
                      onChange={(e) => setReqDueDate(e.target.value)}
                      className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-brand/20"
                    />
                  </div>

                  <div>
                    <label className="block font-bold text-gray-800 mb-1">Notification Email Note</label>
                    <textarea
                      rows={2}
                      value={reqNote}
                      onChange={(e) => setReqNote(e.target.value)}
                      className="w-full p-2 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-brand/20"
                    />
                  </div>
                </div>

                <div className="pt-3 border-t border-gray-100 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    disabled={isSendingRequest}
                    onClick={() => !isSendingRequest && setShowRequestModal(false)}
                    className="px-4 py-2 rounded-xl bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSendingRequest}
                    className="px-5 py-2 rounded-xl bg-brand text-white font-bold hover:bg-brand-600 shadow-md flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isSendingRequest ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Sending Request Email...</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        <span>Send Request Email & Track</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  }

  // RENDER MAIN CANDIDATE DOCUMENTATION LIST PAGE VIEW
  return (
    <div className="space-y-6 pb-12">
      <CustomAlert
        isOpen={alertState.isOpen}
        message={alertState.message}
        type={alertState.type}
        onClose={hideAlert}
      />

      {/* Header Banner - White Background */}
      <div className="relative overflow-hidden rounded-2xl bg-white text-gray-900 p-4 sm:p-6 shadow-sm border border-gray-100">
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4 sm:gap-6">
          <div className="space-y-2">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-extrabold tracking-tight text-gray-900">
              Candidate Documentation & Verification
            </h1>
            <p className="text-xs sm:text-sm text-gray-600 max-w-2xl">
              Inspect, audit, verify, and track all uploaded candidate resumes, identity proof cards, academic degrees, experience letters, and onboarding documents.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2.5 sm:gap-3">
            <button
              onClick={() => setShowUploadModal(true)}
              className="inline-flex items-center gap-2 px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-xl bg-brand text-white font-semibold text-xs sm:text-sm shadow-sm hover:bg-brand-600 transition-all hover:scale-105 active:scale-95"
            >
              <Upload className="w-4 h-4 text-white" />
              <span>Upload Document</span>
            </button>

            <button
              onClick={() => setShowRequestModal(true)}
              className="inline-flex items-center gap-2 px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-xl bg-gray-50 hover:bg-gray-100 text-gray-800 font-semibold text-xs sm:text-sm shadow-sm border border-gray-200 transition-all"
            >
              <Send className="w-4 h-4 text-gray-600" />
              <span>Request Documents</span>
            </button>

            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-2 sm:p-2.5 rounded-xl bg-gray-50 hover:bg-gray-100 text-gray-700 transition-all border border-gray-200"
              title="Refresh Data"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* KPI Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3.5 sm:gap-4">
        {/* Total Docs */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-all">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Documents</span>
            <div className="p-2 rounded-xl bg-blue-50 text-brand">
              <FileText className="w-5 h-5" />
            </div>
          </div>
          <p className="text-2xl font-black text-gray-900 mt-2">{metrics.total}</p>
          <p className="text-[11px] text-gray-400 mt-1">Across all candidates</p>
        </div>

        {/* Verified */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-all">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Verified & Approved</span>
            <div className="p-2 rounded-xl bg-emerald-50 text-emerald-600">
              <CheckCircle2 className="w-5 h-5" />
            </div>
          </div>
          <p className="text-2xl font-black text-emerald-600 mt-2">{metrics.verified}</p>
          <p className="text-[11px] text-emerald-600 font-medium mt-1">Verified compliant</p>
        </div>

        {/* Pending */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-all">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Pending Review</span>
            <div className="p-2 rounded-xl bg-amber-50 text-amber-600">
              <Clock className="w-5 h-5" />
            </div>
          </div>
          <p className="text-2xl font-black text-amber-600 mt-2">{metrics.pending}</p>
          <p className="text-[11px] text-amber-600 font-medium mt-1">Requires HR verification</p>
        </div>

        {/* Rejected */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-all">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Rejected</span>
            <div className="p-2 rounded-xl bg-rose-50 text-rose-600">
              <XCircle className="w-5 h-5" />
            </div>
          </div>
          <p className="text-2xl font-black text-rose-600 mt-2">{metrics.rejected}</p>
          <p className="text-[11px] text-rose-600 font-medium mt-1">Action required</p>
        </div>

        {/* Completion Rate */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-all col-span-1 sm:col-span-2 lg:col-span-1">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Verification Rate</span>
            <div className="p-2 rounded-xl bg-brand-50 text-brand">
              <ShieldCheck className="w-5 h-5" />
            </div>
          </div>
          <p className="text-2xl font-black text-brand mt-2">{metrics.completionRate}%</p>
          <div className="w-full bg-gray-100 h-1.5 rounded-full mt-2 overflow-hidden">
            <div className="bg-brand h-full rounded-full transition-all duration-500" style={{ width: `${metrics.completionRate}%` }} />
          </div>
        </div>
      </div>

      {/* Control Bar: Search, Filters & View Switches */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 space-y-4">
        <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3.5 sm:gap-4">
          {/* Search Box */}
          <div className="relative w-full lg:w-96 flex-1 min-w-[200px]">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search candidate, email, file name..."
              className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand transition-all"
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Filters & View Controls */}
          <div className="flex flex-wrap items-center gap-2.5 sm:gap-3 w-full lg:w-auto justify-between lg:justify-end">
            {/* Category Dropdown */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full sm:w-auto px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs sm:text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand/20 cursor-pointer"
            >
              {DOCUMENT_CATEGORIES.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.label}</option>
              ))}
            </select>

            {/* Status Dropdown */}
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full sm:w-auto px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs sm:text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand/20 cursor-pointer"
            >
              {STATUS_FILTERS.map(st => (
                <option key={st.id} value={st.id}>{st.label}</option>
              ))}
            </select>

            {/* View Mode Toggle Buttons */}
            <div className="flex items-center bg-gray-100 p-1 rounded-xl border border-gray-200 w-full sm:w-auto justify-center">
              <button
                onClick={() => setViewMode('grouped')}
                className={`p-1.5 px-2.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${viewMode === 'grouped' ? 'bg-white text-brand shadow-sm font-bold' : 'text-gray-500 hover:text-gray-700'}`}
                title="Single Candidate Grouped View"
              >
                <User className="w-4 h-4" />
                <span className="whitespace-nowrap">Single Candidate View</span>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 px-2.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${viewMode === 'list' ? 'bg-white text-brand shadow-sm font-bold' : 'text-gray-500 hover:text-gray-700'}`}
                title="Flat Document List View"
              >
                <List className="w-4 h-4" />
                <span className="whitespace-nowrap">Flat List</span>
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 px-2.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${viewMode === 'grid' ? 'bg-white text-brand shadow-sm font-bold' : 'text-gray-500 hover:text-gray-700'}`}
                title="Grid Cards View"
              >
                <Grid className="w-4 h-4" />
                <span className="whitespace-nowrap">Cards</span>
              </button>
            </div>
          </div>
        </div>

        {/* Bulk Action Toolbar */}
        {selectedDocIds.length > 0 && (
          <div className="flex items-center justify-between px-4 py-2.5 bg-brand-50 border border-brand-100 rounded-xl text-sm flex-wrap gap-2">
            <span className="font-semibold text-brand-900">
              {selectedDocIds.length} document{selectedDocIds.length > 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={handleBulkApprove}
                className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white font-medium text-xs hover:bg-emerald-700 transition-all shadow-sm flex items-center gap-1"
              >
                <CheckCircle2 className="w-3.5 h-3.5" /> Approve Selected
              </button>
              <button
                onClick={() => setSelectedDocIds([])}
                className="px-3 py-1.5 rounded-lg bg-gray-200 text-gray-700 font-medium text-xs hover:bg-gray-300 transition-all"
              >
                Deselect
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Main Content Area */}
      {loading ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-gray-100 shadow-sm space-y-3">
          <RefreshCw className="w-8 h-8 text-brand animate-spin mx-auto" />
          <p className="text-sm font-semibold text-gray-600">Loading candidate documents & verification statuses...</p>
        </div>
      ) : filteredDocuments.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-gray-100 shadow-sm space-y-4">
          <FileText className="w-12 h-12 text-gray-300 mx-auto" />
          <div>
            <h3 className="text-base font-bold text-gray-800">No Candidate Documents Found</h3>
            <p className="text-xs text-gray-500 mt-1 max-w-md mx-auto">
              No uploaded documents match your search parameters. Try clearing filters or upload a new candidate document.
            </p>
          </div>
          <button
            onClick={() => { setSearchTerm(''); setSelectedCategory('all'); setSelectedStatus('all'); }}
            className="px-4 py-2 bg-brand text-white font-semibold text-xs rounded-xl shadow hover:bg-brand-600 transition-all"
          >
            Reset Filters
          </button>
        </div>
      ) : viewMode === 'grouped' ? (
        /* MINIMAL CLEAN CANDIDATE MAIN TABLE VIEW (MATCHES OFFERS / INTERVIEWS TAB LAYOUT) */
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto custom-scrollbar">
            <table className="w-full text-left border-collapse min-w-[960px]">
              <thead>
                <tr className="bg-[#edf4ff] border-b border-blue-100 text-xs font-bold text-slate-700 uppercase tracking-wider">
                  <th className="py-3.5 px-4 w-10 min-w-[40px]">
                    <input
                      type="checkbox"
                      checked={selectedDocIds.length > 0 && candidateGroupedList.every(c => c.documents.every(d => selectedDocIds.includes(d.id)))}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-brand focus:ring-brand"
                    />
                  </th>
                  <th className="py-3.5 px-4 min-w-[220px]">Candidate <span className="text-slate-400 font-normal ml-0.5">↑↓</span></th>
                  <th className="py-3.5 px-4 min-w-[180px]">Job Position <span className="text-slate-400 font-normal ml-0.5">↑↓</span></th>
                  <th className="py-3.5 px-4 min-w-[150px]">Total Documents <span className="text-slate-400 font-normal ml-0.5">↑↓</span></th>
                  <th className="py-3.5 px-4 min-w-[200px]">Verification Audit <span className="text-slate-400 font-normal ml-0.5">↑↓</span></th>
                  <th className="py-3.5 px-4 min-w-[160px]">Status <span className="text-slate-400 font-normal ml-0.5">↑↓</span></th>
                  <th className="py-3.5 px-4 min-w-[160px] text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-xs">
                {paginatedCandidateGroups.map(candGroup => {
                  const candDocs = candGroup.documents;
                  const totalCandDocs = candDocs.length;
                  const verifiedCount = candDocs.filter(d => d.status === 'approved').length;
                  const pendingCount = candDocs.filter(d => d.status === 'pending').length;
                  const rejectedCount = candDocs.filter(d => d.status === 'rejected').length;
                  const percent = totalCandDocs > 0 ? Math.round((verifiedCount / totalCandDocs) * 100) : 0;
                  const allSelected = candDocs.every(d => selectedDocIds.includes(d.id));

                  // Determine overall candidate documentation status badge
                  let candStatusBadge = (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 whitespace-nowrap">
                      <Clock className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" /> Pending Review
                    </span>
                  );
                  if (rejectedCount > 0) {
                    candStatusBadge = (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200 whitespace-nowrap">
                        <XCircle className="w-3.5 h-3.5 text-rose-600 flex-shrink-0" /> Action Required
                      </span>
                    );
                  } else if (percent === 100 && totalCandDocs > 0) {
                    candStatusBadge = (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 whitespace-nowrap">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" /> Verified Compliant
                      </span>
                    );
                  }

                  return (
                    <tr key={candGroup.key} className="hover:bg-blue-50/20 transition-colors">
                      {/* Checkbox */}
                      <td className="py-3.5 px-4">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={(e) => handleSelectAllCandidateDocs(candDocs, e)}
                          className="rounded border-gray-300 text-brand focus:ring-brand"
                        />
                      </td>

                      {/* Candidate Name & Email */}
                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-3 min-w-0">
                          <div
                            onClick={() => handleOpenCandidateDossier(candGroup)}
                            className="w-9 h-9 rounded-full bg-brand-100 text-brand font-bold flex items-center justify-center text-xs shadow-inner cursor-pointer hover:scale-105 transition-transform flex-shrink-0"
                          >
                            {candGroup.candidateName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p
                              onClick={() => handleOpenCandidateDossier(candGroup)}
                              className="font-extrabold text-gray-900 hover:text-brand transition-colors cursor-pointer text-sm flex items-center gap-1 truncate"
                              title={candGroup.candidateName}
                            >
                              <span>{candGroup.candidateName}</span>
                            </p>
                            <p className="text-[11px] text-gray-500 font-medium truncate" title={candGroup.candidateEmail}>{candGroup.candidateEmail}</p>
                          </div>
                        </div>
                      </td>

                      {/* Job Position & Stage */}
                      <td className="py-3.5 px-4">
                        <div className="space-y-1 min-w-0">
                          <p className="font-bold text-gray-800 text-xs truncate" title={candGroup.jobTitle}>{candGroup.jobTitle}</p>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[11px] text-gray-500">{candGroup.department}</span>
                            <span>•</span>
                            {getStageBadge(candGroup.candidateStage)}
                          </div>
                        </div>
                      </td>

                      {/* Total Uploaded Documents */}
                      <td className="py-3.5 px-4">
                        <span className="px-3 py-1 rounded-full bg-blue-50 text-brand font-extrabold text-xs border border-blue-100 inline-flex items-center gap-1 whitespace-nowrap">
                          <FileText className="w-3.5 h-3.5 text-brand flex-shrink-0" />
                          <span>{totalCandDocs} Uploaded {totalCandDocs === 1 ? 'Doc' : 'Docs'}</span>
                        </span>
                      </td>

                      {/* Verification Audit Rate */}
                      <td className="py-3.5 px-4">
                        <div className="space-y-1 max-w-xs">
                          <div className="flex items-center justify-between text-xs">
                            <span className="font-bold text-gray-700">Audit Score</span>
                            <span className="font-black text-brand">{percent}%</span>
                          </div>
                          <div className="w-32 bg-gray-200 h-2 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all duration-500 ${percent === 100 ? 'bg-emerald-500' : 'bg-brand'}`}
                              style={{ width: `${percent}%` }}
                            />
                          </div>
                          <p className="text-[10px] text-gray-500 whitespace-nowrap">
                            {verifiedCount} Verified • {pendingCount} Pending • {rejectedCount} Rejected
                          </p>
                        </div>
                      </td>

                      {/* Candidate Verification Status */}
                      <td className="py-3.5 px-4">
                        {candStatusBadge}
                      </td>

                      {/* Action Buttons */}
                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5 flex-nowrap">
                          {/* Eye / View Details Button */}
                          <button
                            onClick={() => handleOpenCandidateDossier(candGroup)}
                            className="p-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs transition-all shadow-sm flex items-center justify-center flex-shrink-0"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>

                          {/* Upload File */}
                          <button
                            onClick={() => { setUploadCandidateId(candGroup.candidateId); setShowUploadModal(true); }}
                            className="p-2 rounded-xl bg-brand-50 hover:bg-brand-100 text-brand font-bold text-xs transition-all flex-shrink-0"
                            title="Upload Candidate Document File"
                          >
                            <Plus className="w-4 h-4" />
                          </button>

                          {/* Request Document */}
                          <button
                            onClick={() => { setReqCandidateId(candGroup.candidateId); setShowRequestModal(true); }}
                            className="p-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold text-xs transition-all flex-shrink-0"
                            title="Send Document Request Email"
                          >
                            <Send className="w-4 h-4" />
                          </button>

                          {/* Approve All */}
                          {pendingCount > 0 && (
                            <button
                              onClick={() => handleApproveAllForCandidate(candDocs)}
                              className="p-2 rounded-xl bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-bold text-xs border border-emerald-200 transition-all flex-shrink-0"
                              title="Approve All Pending Documents"
                            >
                              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : viewMode === 'list' ? (
        /* FLAT TABLE VIEW */
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto custom-scrollbar">
            <table className="w-full text-left border-collapse min-w-[960px]">
              <thead>
                <tr className="bg-[#edf4ff] border-b border-blue-100 text-xs font-bold text-slate-700 uppercase tracking-wider">
                  <th className="py-3.5 px-4 w-10 min-w-[40px]">
                    <input
                      type="checkbox"
                      checked={selectedDocIds.length > 0 && selectedDocIds.length === paginatedFlatDocuments.length}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-brand focus:ring-brand"
                    />
                  </th>
                  <th className="py-3.5 px-4">Candidate <span className="text-slate-400 font-normal ml-0.5">↑↓</span></th>
                  <th className="py-3.5 px-4">Document Title & Category <span className="text-slate-400 font-normal ml-0.5">↑↓</span></th>
                  <th className="py-3.5 px-4">Upload Info <span className="text-slate-400 font-normal ml-0.5">↑↓</span></th>
                  <th className="py-3.5 px-4">Status <span className="text-slate-400 font-normal ml-0.5">↑↓</span></th>
                  <th className="py-3.5 px-4">Verification Audit</th>
                  <th className="py-3.5 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-sm">
                {paginatedFlatDocuments.map(doc => {
                  const isSelected = selectedDocIds.includes(doc.id);
                  return (
                    <tr key={doc.id} className={`hover:bg-blue-50/30 transition-colors ${isSelected ? 'bg-brand-50/40' : ''}`}>
                      <td className="py-3.5 px-4">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSelectOne(doc.id)}
                          className="rounded border-gray-300 text-brand focus:ring-brand"
                        />
                      </td>
                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-brand-100 text-brand font-bold flex items-center justify-center text-xs shadow-inner">
                            {doc.candidateName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-bold text-gray-900 hover:text-brand transition-colors cursor-pointer" onClick={() => setPreviewDocument(doc)}>
                              {doc.candidateName}
                            </p>
                            <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                              <span>{doc.jobTitle}</span>
                              <span>•</span>
                              {getStageBadge(doc.candidateStage)}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="py-3.5 px-4">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 font-semibold text-gray-800">
                            <FileText className="w-4 h-4 text-brand shrink-0" />
                            <span className="truncate max-w-xs">{doc.documentName}</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-700 font-medium text-[10px]">
                              {getNormalizedFileType(doc)} • {doc.fileSize}
                            </span>
                            <span className="text-gray-400">|</span>
                            <span className="text-gray-600">{doc.categoryLabel}</span>
                          </div>
                        </div>
                      </td>

                      <td className="py-3.5 px-4">
                        <div className="text-xs text-gray-600 space-y-0.5">
                          <div className="flex items-center gap-1 font-medium">
                            <Clock className="w-3.5 h-3.5 text-gray-400" />
                            <span>{formatDisplayDate(doc.uploadedAt)}</span>
                          </div>
                          <p className="text-[11px] text-gray-400">{doc.candidateEmail}</p>
                        </div>
                      </td>

                      <td className="py-3.5 px-4">
                        {getStatusBadge(doc.status)}
                      </td>

                      <td className="py-3.5 px-4">
                        <div className="text-xs space-y-0.5">
                          {doc.verifiedBy ? (
                            <p className="font-medium text-gray-700">Verified by <span className="text-gray-900 font-semibold">{doc.verifiedBy}</span></p>
                          ) : (
                            <p className="text-gray-400 italic">Unverified</p>
                          )}
                          {doc.verificationNotes && (
                            <p className="text-[11px] text-gray-500 truncate max-w-xs">{doc.verificationNotes}</p>
                          )}
                        </div>
                      </td>

                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setPreviewDocument(doc)}
                            className="p-1.5 rounded-lg text-gray-600 hover:text-brand hover:bg-brand-50 transition-all"
                            title="Preview Document"
                          >
                            <Eye className="w-4 h-4" />
                          </button>

                          <button
                            onClick={() => handleDownloadFile(doc)}
                            className="p-1.5 rounded-lg text-gray-600 hover:text-brand hover:bg-brand-50 transition-all"
                            title="Download Document"
                          >
                            <Download className="w-4 h-4" />
                          </button>

                          {doc.status !== 'approved' && (
                            <button
                              onClick={() => handleApproveDocument(doc.id)}
                              className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50 transition-all"
                              title="Approve Document"
                            >
                              <CheckCircle2 className="w-4 h-4" />
                            </button>
                          )}

                          {doc.status !== 'rejected' && (
                            <button
                              onClick={() => handleOpenRejectModal(doc)}
                              className="p-1.5 rounded-lg text-rose-600 hover:bg-rose-50 transition-all"
                              title="Reject Document"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* VISUAL GRID CARD VIEW */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {paginatedFlatDocuments.map(doc => (
            <div key={doc.id} className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-all flex flex-col justify-between space-y-4">
              <div className="space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-brand-50 border border-brand-100 text-brand">
                      <FileText className="w-6 h-6" />
                    </div>
                    <div>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{getNormalizedFileType(doc)} FILE</span>
                      <h4 className="text-sm font-bold text-gray-900 line-clamp-1">{doc.documentName}</h4>
                    </div>
                  </div>
                  {getStatusBadge(doc.status)}
                </div>

                <div className="p-3 rounded-xl bg-gray-50 border border-gray-100 space-y-1 text-xs">
                  <div className="flex items-center justify-between text-gray-600">
                    <span className="font-semibold text-gray-800">{doc.candidateName}</span>
                    <span>{getStageBadge(doc.candidateStage)}</span>
                  </div>
                  <p className="text-[11px] text-gray-500">{doc.jobTitle} • {doc.candidateEmail}</p>
                </div>

                <div className="text-[11px] text-gray-400 space-y-0.5">
                  <p>Uploaded: {formatDisplayDate(doc.uploadedAt)}</p>
                  {doc.verifiedBy && <p className="text-emerald-700 font-medium">Verified by: {doc.verifiedBy}</p>}
                </div>
              </div>

              <div className="pt-3 border-t border-gray-100 flex items-center justify-between">
                <span className="text-xs font-medium text-gray-500">{doc.fileSize}</span>
                <div className="flex items-center gap-1">
                  <button onClick={() => setPreviewDocument(doc)} className="p-2 rounded-lg bg-gray-50 text-gray-700 hover:bg-brand-50 hover:text-brand font-semibold text-xs flex items-center gap-1 transition-all">
                    <Eye className="w-3.5 h-3.5" /> View
                  </button>
                  <button onClick={() => handleDownloadFile(doc)} className="p-2 rounded-lg bg-gray-50 text-gray-700 hover:bg-brand-50 hover:text-brand font-semibold text-xs transition-all">
                    <Download className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination Footer */}
      <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          limit={itemsPerPage}
          onLimitChange={setItemsPerPage}
          totalItems={totalItemsCount}
        />
      </div>

      {/* UPLOAD DOCUMENT MODAL */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 bg-transparent flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl p-6 space-y-5 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <div className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-brand" />
                <h3 className="font-bold text-base text-gray-900">Upload Candidate Document</h3>
              </div>
              <button onClick={() => setShowUploadModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateUpload} className="space-y-4 text-xs">
              <div>
                <label className="block font-bold text-gray-700 mb-1">Select Candidate (Completed Interviews Only) *</label>
                <select
                  value={uploadCandidateId}
                  onChange={(e) => setUploadCandidateId(e.target.value)}
                  className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-brand/20"
                  required
                >
                  <option value="">-- Choose Candidate with Completed Interview --</option>
                  {completedInterviewCandidates.length > 0 ? (
                    completedInterviewCandidates.map(c => (
                      <option key={c._id || c.id || c.candidateId} value={c._id || c.id || c.candidateId}>
                        {c.firstName} {c.lastName} ({c.jobTitle || 'Candidate'}) [Interview Completed]
                      </option>
                    ))
                  ) : (
                    <>
                      <option value="cand_1">Salman Khan (Software Engineer) [Interview Completed]</option>
                      <option value="cand_2">Dummy Candidate (UI/UX Designer) [Interview Completed]</option>
                      <option value="cand_3">Prince Kumar (Full Stack Developer) [Interview Completed]</option>
                    </>
                  )}
                </select>
              </div>

              <div>
                <label className="block font-bold text-gray-700 mb-1">Document Category *</label>
                <select
                  value={uploadCategory}
                  onChange={(e) => setUploadCategory(e.target.value)}
                  className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-brand/20"
                >
                  <option value="identity">Identity Proof (PAN / Aadhar / Passport)</option>
                  <option value="education">Degree & Academic Certificates</option>
                  <option value="experience">Experience / Relieving Letters</option>
                  <option value="offer">Offer Letter & Contract</option>
                  <option value="bank">Bank & Salary Documents</option>
                  <option value="resume">Resume / CV</option>
                  <option value="other">Other Document</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-gray-700 mb-1">Document Name / Title *</label>
                <input
                  type="text"
                  value={uploadDocName}
                  onChange={(e) => setUploadDocName(e.target.value)}
                  placeholder="e.g. PAN_Card_Verified.pdf"
                  className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-brand/20"
                  required
                />
              </div>

              <div>
                <label className="block font-bold text-gray-700 mb-1">Choose File</label>
                <input
                  type="file"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                  className="w-full p-2 bg-gray-50 border border-gray-200 rounded-xl text-xs"
                />
              </div>

              <div className="pt-3 border-t border-gray-100 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="px-4 py-2 rounded-xl bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 rounded-xl bg-brand text-white font-bold hover:bg-brand-600 shadow-md"
                >
                  Upload & Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* REJECT REASON MODAL */}
      {showRejectModal && rejectingDoc && (
        <div className="fixed inset-0 z-50 bg-transparent flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl p-6 space-y-4 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <div className="flex items-center gap-2 text-rose-600">
                <XCircle className="w-5 h-5" />
                <h3 className="font-bold text-base text-gray-900">Reject Document Verification</h3>
              </div>
              <button onClick={() => setShowRejectModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <p className="text-gray-600">
                Rejecting <span className="font-bold text-gray-900">{rejectingDoc.documentName}</span> uploaded by <span className="font-bold text-gray-900">{rejectingDoc.candidateName}</span>.
              </p>

              <div>
                <label className="block font-bold text-gray-700 mb-1">Rejection Reason & Feedback Notes *</label>
                <textarea
                  rows={3}
                  value={rejectionNotes}
                  onChange={(e) => setRejectionNotes(e.target.value)}
                  placeholder="Provide feedback on why this document is rejected..."
                  className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-rose-500/20"
                />
              </div>

              <div className="pt-3 border-t border-gray-100 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowRejectModal(false)}
                  className="px-4 py-2 rounded-xl bg-gray-100 text-gray-700 font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmitRejection}
                  className="px-5 py-2 rounded-xl bg-rose-600 text-white font-bold hover:bg-rose-700 shadow-md"
                >
                  Confirm Rejection
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ENHANCED REQUEST DOCUMENTS FROM CANDIDATE MODAL */}
      {showRequestModal && (
        <div className="fixed inset-0 z-50 bg-transparent flex items-center justify-center p-4">
          <div className="bg-white border border-gray-200 rounded-3xl w-full max-w-2xl shadow-2xl p-6 space-y-5 animate-in fade-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <div className="flex items-center gap-2.5 text-brand">
                <div className="p-2 rounded-xl bg-brand-50 text-brand">
                  <Send className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-extrabold text-base text-gray-900">Request Documents from Candidate</h3>
                  <p className="text-xs text-gray-500">Select candidate and check required documents to request via email</p>
                </div>
              </div>
              <button onClick={() => setShowRequestModal(false)} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSendRequest} className="space-y-4 text-xs overflow-y-auto flex-1 pr-1">
              <div>
                <label className="block font-bold text-gray-800 mb-1">
                  Select Target Candidate (Only Candidates with Completed Interviews) *
                </label>
                <select
                  value={reqCandidateId}
                  onChange={(e) => setReqCandidateId(e.target.value)}
                  className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-brand/20"
                  required
                >
                  <option value="">-- Choose Candidate with Completed Interview --</option>
                  {completedInterviewCandidates.length > 0 ? (
                    completedInterviewCandidates.map(c => (
                      <option key={c._id || c.id || c.candidateId} value={c._id || c.id || c.candidateId}>
                        {c.firstName} {c.lastName} ({c.email || 'No email'}) - {c.jobTitle || 'Candidate'} [Interview Completed]
                      </option>
                    ))
                  ) : (
                    candidatesList.map(c => (
                      <option key={c._id || c.id || c.candidateId} value={c._id || c.id || c.candidateId}>
                        {c.firstName} {c.lastName} ({c.email || 'No email'}) - {c.jobTitle || 'Candidate'}
                      </option>
                    ))
                  )}
                </select>
                {completedInterviewCandidates.length === 0 && candidatesList.length > 0 && (
                  <p className="text-[11px] text-amber-600 font-semibold mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" />
                    No candidates currently have completed interviews. Complete an interview first to request documents.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="block font-bold text-gray-800">
                    Select Documents to Request ({selectedReqDocIds.length} Selected) *
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleSelectAllReqDocs}
                      className="text-xs text-brand font-bold hover:underline"
                    >
                      Select All Master Documents
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      type="button"
                      onClick={handleClearAllReqDocs}
                      className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
                    >
                      Clear All
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 bg-slate-50/80 p-3.5 rounded-2xl border border-gray-200 max-h-56 overflow-y-auto">
                  {availableRequestDocuments.map(doc => {
                    const isChecked = selectedReqDocIds.includes(doc.id);
                    return (
                      <label
                        key={doc.id}
                        className={`flex items-start gap-2.5 p-2.5 rounded-xl border transition-all cursor-pointer ${isChecked ? 'bg-brand-50/70 border-brand-200 text-brand-900 shadow-sm' : 'bg-white border-gray-200 hover:border-gray-300 text-gray-700'}`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleReqDocCheckbox(doc.id)}
                          className="mt-0.5 rounded border-gray-300 text-brand focus:ring-brand"
                        />
                        <div className="min-w-0">
                          <div className="font-bold text-xs leading-snug flex items-center gap-1.5">
                            <span>{doc.name}</span>
                            {doc.category === 'master' && (
                              <span className="px-1.5 py-0.5 rounded text-[9px] font-extrabold bg-blue-100 text-blue-700 uppercase">
                                Master
                              </span>
                            )}
                          </div>
                          <span className="text-[10px] text-gray-500 font-medium block mt-0.5">{doc.categoryLabel}</span>
                        </div>
                      </label>
                    );
                  })}

                  {customReqDocsList.map(cDoc => {
                    const isChecked = selectedReqDocIds.includes(cDoc.id);
                    return (
                      <label
                        key={cDoc.id}
                        className={`flex items-start gap-2.5 p-2.5 rounded-xl border transition-all cursor-pointer ${isChecked ? 'bg-purple-50 border-purple-200 text-purple-900 shadow-sm' : 'bg-white border-gray-200 text-gray-700'}`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleReqDocCheckbox(cDoc.id)}
                          className="mt-0.5 rounded border-purple-400 text-purple-600 focus:ring-purple-500"
                        />
                        <div className="min-w-0">
                          <p className="font-bold text-xs leading-snug">{cDoc.name}</p>
                          <span className="text-[10px] text-purple-600 font-semibold block mt-0.5">Custom Requirement</span>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={customReqDocName}
                  onChange={(e) => setCustomReqDocName(e.target.value)}
                  placeholder="Add custom document requirement..."
                  className="flex-1 p-2 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-brand/20"
                />
                <button
                  type="button"
                  onClick={handleAddCustomReqDoc}
                  className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold text-xs rounded-xl transition-all"
                >
                  + Add Custom
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-gray-800 mb-1">Submission Deadline / Due Date</label>
                  <input
                    type="date"
                    value={reqDueDate}
                    onChange={(e) => setReqDueDate(e.target.value)}
                    className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-brand/20"
                  />
                </div>

                <div>
                  <label className="block font-bold text-gray-800 mb-1">Notification Email Note</label>
                  <textarea
                    rows={2}
                    value={reqNote}
                    onChange={(e) => setReqNote(e.target.value)}
                    className="w-full p-2 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-brand/20"
                  />
                </div>
              </div>

              <div className="pt-3 border-t border-gray-100 flex items-center justify-end gap-2">
                <button
                  type="button"
                  disabled={isSendingRequest}
                  onClick={() => !isSendingRequest && setShowRequestModal(false)}
                  className="px-4 py-2 rounded-xl bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSendingRequest}
                  className="px-5 py-2 rounded-xl bg-brand text-white font-bold hover:bg-brand-600 shadow-md flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isSendingRequest ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Sending Request Email...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span>Send Request Email & Track</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CandidateDocumentation;
